import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, CheckCircle, Moon, Sun, Palette, Bell, Shield, Globe, CreditCard, Users, Building2, FileText, Smartphone, Menu, ChevronRight, ChevronDown, Mail, MessageSquare, MessageCircle, ExternalLink, Edit3, Percent, Code, Upload, Trash2, Image, Loader2, Star, Sparkles, Crown, ArrowRight, FileSignature, Save, X, BellRing, Plus, Key, Copy, AlertTriangle, MapPin, Navigation, Download, Archive, Database, Eye, EyeOff, Rocket } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import TemplateEditor from '../components/TemplateEditor';
import EmailAssistantGuidedSetup from '../components/EmailAssistantGuidedSetup';
import ClickSendSenderPanel from '../components/ClickSendSenderPanel';
import EmailDomainPanel from '../components/EmailDomainPanel';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to adjust color brightness for gradients
const adjustColor = (color, amount) => {
  const clamp = (num) => Math.min(255, Math.max(0, num));
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = clamp((num >> 16) + amount);
  const g = clamp(((num >> 8) & 0x00FF) + amount);
  const b = clamp((num & 0x0000FF) + amount);
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
};

const SettingRow = ({ title, subtitle, action, actionText = 'Edit', onAction }) => (
  <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-xl">
    <div>
      <p className="font-medium text-zinc-900">{title}</p>
      <p className="text-sm text-zinc-500">{subtitle}</p>
    </div>
    {action ? action : (
      <button 
        onClick={onAction}
        className={`text-sm font-medium ${onAction ? 'text-emerald-600 hover:text-emerald-700 cursor-pointer' : 'text-zinc-400 cursor-not-allowed'}`}
        disabled={!onAction}
      >
        {actionText}
      </button>
    )}
  </div>
);

// Email Forwarding Component for adding customers via email
const EmailForwardingSection = () => {
  const [emailToken, setEmailToken] = useState(null);
  const [emailAddress, setEmailAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load existing token on mount
  useEffect(() => {
    const loadToken = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/settings/email-token`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEmailToken(data.token);
          setEmailAddress(data.email_address);
        }
      } catch (err) {
        console.error('Error loading email token:', err);
      } finally {
        setLoading(false);
      }
    };
    loadToken();
  }, []);

  // Generate new token
  const generateToken = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/settings/generate-email-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmailToken(data.token);
        setEmailAddress(data.email_address);
      }
    } catch (err) {
      console.error('Error generating email token:', err);
    } finally {
      setGenerating(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    if (emailAddress) {
      navigator.clipboard.writeText(emailAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 mb-4">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white">
          <Mail size={20} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-zinc-900">Email to Customer</h3>
          <p className="text-sm text-zinc-600">
            Forward emails to automatically add customers to your CRM
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 size={16} className="animate-spin" />
          Loading your email address...
        </div>
      ) : emailAddress ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-200">
            <div className="flex-1">
              <p className="text-xs text-zinc-500 mb-1">Your forwarding address:</p>
              <p className="font-mono text-sm text-blue-700 font-medium break-all">{emailAddress}</p>
            </div>
            <button
              onClick={copyToClipboard}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                copied 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
              data-testid="copy-email-btn"
            >
              {copied ? (
                <span className="flex items-center gap-1">
                  <Check size={14} /> Copied!
                </span>
              ) : (
                'Copy'
              )}
            </button>
          </div>
          <div className="p-3 bg-white/50 rounded-lg space-y-2">
            <p className="text-xs text-zinc-600">
              <strong>How it works:</strong> Forward any email containing customer details (name, address, phone, email) to this address. 
              The system will automatically extract the information and create a new customer in your CRM.
            </p>
            <p className="text-xs text-zinc-600">
              <strong>PDF Import:</strong> Attach a PDF containing a customer list to bulk import multiple customers at once.
              The system will extract names, phone numbers, emails and addresses from your PDF.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 size={16} className="animate-spin" />
          Generating your unique email address...
        </div>
      )}
    </div>
  );
};

// Push Notifications Section
const EmailAssistantSection = () => {
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showGuided, setShowGuided] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/email-assistant/address`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAddress(data.email_address);
        }
      } catch (err) {
        console.error('Error loading assistant address:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copyAddress = () => {
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4" data-testid="email-assistant-section">
      <div className="p-4 bg-sky-50 border border-sky-200 rounded-xl">
        <p className="text-sm font-semibold text-sky-900 mb-1">Auto-answer "when's my next clean?"</p>
        <p className="text-xs text-sky-800 leading-relaxed">
          Forward your business email through StreakFree and we'll automatically reply to customers asking when their next
          appointment is — always with an <strong>"on or around"</strong> date, never a fixed one, since appointments can move
          with weather, annual leave and other jobs. Every other email is left alone.
        </p>
      </div>

      <button
        onClick={() => setShowGuided(true)}
        className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold"
        data-testid="email-assistant-guided-btn"
      >
        Guided setup — 2 minutes
      </button>

      <div>
        <p className="text-xs font-semibold text-zinc-600 mb-1.5">Your forwarding address</p>
        {loading ? (
          <div className="h-11 bg-zinc-100 rounded-xl animate-pulse" />
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={address || ''}
              className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm font-mono text-zinc-700 truncate"
              data-testid="email-assistant-address"
            />
            <button
              onClick={copyAddress}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold ${copied ? 'bg-emerald-500 text-white' : 'bg-zinc-900 text-white'}`}
              data-testid="email-assistant-copy-btn"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2" data-testid="email-assistant-instructions">
        <p className="text-xs font-semibold text-zinc-600">Set up forwarding (Gmail example)</p>
        {[
          <>In Gmail, click the <strong>⚙ gear</strong> → <strong>See all settings</strong> → <strong>Forwarding and POP/IMAP</strong></>,
          <>Click <strong>Add a forwarding address</strong> and paste the address above</>,
          <>Gmail sends a confirmation — the code will appear in your <strong>StreakFree notifications</strong> within a minute</>,
          <>Choose <strong>"Forward a copy"</strong> and <strong>keep Gmail's copy in the Inbox</strong> — your email works exactly as before</>,
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-zinc-200 text-zinc-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span className="text-xs text-zinc-600 leading-relaxed">{step}</span>
          </div>
        ))}
        <p className="text-[11px] text-zinc-400 pt-1">Outlook: Settings → Mail → Forwarding. Replies are sent from your usual branded email address and clearly note they're automated.</p>
      </div>
      <EmailAssistantGuidedSetup open={showGuided} onClose={() => setShowGuided(false)} />
    </div>
  );
};

const ClaudeConnectorSection = () => {
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState('');
  const mcpUrl = `${API_URL}/api/mcp/`;
  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('streakfree_token')}` });

  const loadKeys = async () => {
    try {
      const r = await fetch(`${API_URL}/api/mcp-keys`, { headers: authHeaders() });
      if (r.ok) setKeys((await r.json()).items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadKeys(); }, []);

  const createKey = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/mcp-keys`, { method: 'POST', headers: authHeaders() });
      if (r.ok) { setNewKey((await r.json()).api_key); loadKeys(); }
    } catch (e) { /* ignore */ }
    setBusy(false);
  };

  const revokeKey = async (id) => {
    await fetch(`${API_URL}/api/mcp-keys/${id}`, { method: 'DELETE', headers: authHeaders() });
    loadKeys();
  };

  const copy = (text, tag) => {
    navigator.clipboard.writeText(text);
    setCopied(tag);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="space-y-4" data-testid="claude-connector-section">
      <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
        <p className="text-sm font-semibold text-orange-900 mb-1">Ask Claude about your business</p>
        <p className="text-xs text-orange-800 leading-relaxed">Add StreakFree as a connector in Claude and ask things like <em>"who owes me money?"</em>, <em>"what's my day look like tomorrow?"</em> or <em>"when is Mrs Smith's next clean?"</em>. Read-only — Claude can look things up but never change your data.</p>
      </div>

      {newKey ? (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2" data-testid="mcp-new-key-box">
          <p className="text-xs font-bold text-emerald-800">Your connector URL is ready — copy it now (shown only once):</p>
          <div className="flex gap-2">
            <input type="text" readOnly value={`${mcpUrl}?api_key=${newKey}`} className="flex-1 px-3 py-2 bg-white border border-emerald-200 rounded-lg text-[11px] font-mono truncate" data-testid="mcp-full-url" />
            <button onClick={() => copy(`${mcpUrl}?api_key=${newKey}`, 'fullurl')} className={`px-3 py-2 rounded-lg text-xs font-bold ${copied === 'fullurl' ? 'bg-emerald-500 text-white' : 'bg-zinc-900 text-white'}`} data-testid="mcp-full-url-copy">{copied === 'fullurl' ? 'Copied!' : 'Copy URL'}</button>
          </div>
          <p className="text-[11px] text-emerald-700 leading-relaxed">This is the <strong>complete URL including your key</strong> — paste the whole thing into Claude's URL box. Nothing else needed.</p>
        </div>
      ) : (
        <button onClick={createKey} disabled={busy} className="w-full py-3 rounded-xl bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-400 text-white text-sm font-bold" data-testid="mcp-create-key-btn">
          {busy ? 'Creating…' : 'Step 1: Create your connector URL'}
        </button>
      )}

      {keys.length > 0 && (
        <div className="space-y-1.5" data-testid="mcp-key-list">
          <p className="text-xs font-semibold text-zinc-600">Active keys</p>
          {keys.map(k => (
            <div key={k.id} className="flex items-center justify-between px-3 py-2 bg-zinc-50 rounded-lg">
              <span className="text-[11px] font-mono text-zinc-600">{k.key_hint}</span>
              <button onClick={() => revokeKey(k.id)} className="text-[11px] font-bold text-red-500" data-testid={`mcp-revoke-${k.id}`}>Revoke</button>
            </div>
          ))}
          <p className="text-[11px] text-zinc-400 leading-relaxed">Lost your URL? Revoke the old key and create a new one — the full URL is only shown once.</p>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-semibold text-zinc-600">How to add it in Claude</p>
        {[
          <>Tap <strong>Create your connector URL</strong> above and copy the full URL it gives you</>,
          <>In Claude, go to <strong>Settings → Connectors → Add custom connector</strong></>,
          <>Name: <strong>StreakFree</strong> — paste the <strong>full URL</strong> (it ends with <span className="font-mono">?api_key=sf_mcp_…</span>) into the URL box</>,
          <>Leave the OAuth fields <strong>blank</strong> and tap Add — then just ask Claude about your customers, jobs and money owed</>,
        ].map((s, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-zinc-200 text-zinc-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span className="text-xs text-zinc-600 leading-relaxed">{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PushNotificationsSection = () => {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    loading, 
    error, 
    toggleSubscription 
  } = usePushNotifications();

  const handleToggle = async () => {
    const success = await toggleSubscription();
    if (success) {
      toast.success(isSubscribed ? 'Push notifications disabled' : 'Push notifications enabled!');
    } else if (error) {
      toast.error(error);
    }
  };

  if (!isSupported) {
    return (
      <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <BellRing className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-zinc-900">Push Notifications</h3>
            <p className="text-sm text-amber-700">
              Not supported in this browser. Use Chrome, Edge or Safari for push notifications.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
          <BellRing className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-zinc-900">Push Notifications</h3>
          <p className="text-sm text-zinc-600">
            Get instant alerts when new customers are added via email
          </p>
        </div>
      </div>
      
      <div className="flex items-center justify-between p-3 bg-white rounded-lg">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-900">
            {isSubscribed ? 'Notifications Enabled' : 'Enable Notifications'}
          </p>
          <p className="text-xs text-zinc-500">
            {permission === 'denied' 
              ? 'Permission blocked — see hint below' 
              : isSubscribed 
                ? 'You\'ll receive alerts for new bookings' 
                : 'Never miss a new customer booking'}
          </p>
          {/* Diagnostic line — surfaces the current browser permission +
              loading state so users (and we) can tell why the toggle won't
              flip when something's blocking it. */}
          <p className="text-[10px] text-zinc-400 mt-0.5">
            Permission: <span className="font-mono">{permission}</span>
            {loading && ' · working…'}
          </p>
        </div>
        <Switch 
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={loading || permission === 'denied'}
          data-testid="push-notifications-toggle"
        />
      </div>
      
      {permission === 'denied' && (
        <div className="mt-2 p-3 bg-amber-100 rounded-lg text-xs text-amber-900 space-y-1">
          <p className="font-semibold">Notifications are blocked at the browser level.</p>
          <p>
            <span className="font-medium">Android:</span> Long-press the app icon → App info → Notifications → enable.
            Then tap the lock icon in the address bar → Permissions → Notifications → Allow.
          </p>
          <p>
            <span className="font-medium">iOS:</span> Settings → StreakFree → Notifications → Allow.
          </p>
          <p>After enabling, refresh this page and tap the toggle again.</p>
        </div>
      )}
      
      {isSubscribed && (
        <div className="mt-2 p-2 bg-emerald-100 rounded-lg">
          <p className="text-xs text-emerald-700 flex items-center gap-1">
            <Check size={12} /> Push notifications active on this device
          </p>
        </div>
      )}
    </div>
  );
};

// Data Export Section
const DataExportSection = () => {
  const [downloading, setDownloading] = useState(null);

  const downloadFile = async (endpoint, fallbackFilename) => {
    try {
      setDownloading(endpoint);
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/export/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        if (res.status === 404) {
          toast.error('No data to export in this category');
        } else {
          toast.error('Download failed. Please try again.');
        }
        return;
      }

      // Extract filename from Content-Disposition header if present
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename=([^;]+)/i);
      const filename = match ? match[1].trim().replace(/"/g, '') : fallbackFilename;

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${filename} downloaded`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const exports = [
    { id: 'customers', label: 'Customers', desc: 'All customer contact details & balances', filename: 'customers.csv', icon: Users, colorClasses: 'bg-emerald-100 text-emerald-600' },
    { id: 'jobs', label: 'Jobs', desc: 'Scheduled, completed & recurring jobs', filename: 'jobs.csv', icon: FileText, colorClasses: 'bg-blue-100 text-blue-600' },
    { id: 'invoices', label: 'Invoices', desc: 'All invoices with status & totals', filename: 'invoices.csv', icon: CreditCard, colorClasses: 'bg-purple-100 text-purple-600' },
    { id: 'payments', label: 'Payments', desc: 'Payment history & methods', filename: 'payments.csv', icon: Percent, colorClasses: 'bg-amber-100 text-amber-600' },
  ];

  return (
    <div className="space-y-3" data-testid="data-export-section">
      {/* Download Everything banner */}
      <div className="p-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl text-white">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Archive className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">Download Everything</h3>
            <p className="text-sm text-white/80">
              Get a ZIP file with all your data — customers, jobs, invoices, and payments.
            </p>
          </div>
        </div>
        <button
          onClick={() => downloadFile('all', `streakfree_export_${new Date().toISOString().slice(0,10)}.zip`)}
          disabled={downloading === 'all'}
          className="w-full py-3 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-emerald-50 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
          data-testid="export-all-zip-btn"
        >
          {downloading === 'all' ? (
            <><Loader2 size={18} className="animate-spin" /> Preparing ZIP...</>
          ) : (
            <><Download size={18} /> Download Full Backup (ZIP)</>
          )}
        </button>
      </div>

      {/* Individual CSV downloads */}
      <div className="p-3 bg-zinc-50 rounded-xl">
        <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3 px-1">
          Or download individual files
        </p>
        <div className="space-y-2">
          {exports.map((exp) => {
            const Icon = exp.icon;
            const isLoading = downloading === exp.id;
            return (
              <button
                key={exp.id}
                onClick={() => downloadFile(exp.id, exp.filename)}
                disabled={isLoading}
                className="w-full flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-zinc-50 transition-colors disabled:opacity-70 text-left"
                data-testid={`export-${exp.id}-btn`}
              >
                <div className={`w-10 h-10 ${exp.colorClasses} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-zinc-900">{exp.label}</p>
                  <p className="text-xs text-zinc-500 truncate">{exp.desc}</p>
                </div>
                {isLoading ? (
                  <Loader2 size={18} className="text-zinc-400 animate-spin flex-shrink-0" />
                ) : (
                  <Download size={18} className="text-zinc-400 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Info banner */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
            <Database size={14} className="text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">CSV-compatible backup</p>
            <p className="text-xs text-blue-700 mt-0.5">
              Files open in Excel, Numbers, Google Sheets, or any CRM (including Squeegee). Keep these backups somewhere safe.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};


// -----------------------------------------------------------------------------
// StripeConnectPanel
// Multi-tenant Stripe connection by API key (mirrors the GoCardless flow).
// Each owner pastes their own sk_live_... here so payment links / auto-charges
// hit THEIR Stripe balance, not a shared platform account.
// -----------------------------------------------------------------------------
const StripeConnectPanel = ({ connected, accountName, environment, onConnected }) => {
  const [expanded, setExpanded] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleConnect = async () => {
    setError('');
    setSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const r = await fetch(`${API_URL}/api/integrations/stripe/connect-api-key`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret_key: secretKey.trim() }),
      });
      const data = await r.json();
      if (r.ok) {
        setSuccess(true);
        setSecretKey('');
        setTimeout(() => {
          setSuccess(false);
          setExpanded(false);
          if (onConnected) onConnected();
        }, 1200);
      } else {
        setError(data.detail || 'Connection failed');
      }
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`p-4 rounded-xl ${connected ? 'bg-emerald-50 border border-emerald-200' : 'bg-zinc-50'}`} data-testid="stripe-connect-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white font-bold">S</div>
          <div>
            <p className="font-medium text-zinc-900">Stripe</p>
            <p className={`text-sm ${connected ? 'text-emerald-600' : 'text-zinc-500'}`}>
              {connected
                ? `Connected${accountName ? ` — ${accountName}` : ''}${environment === 'test' ? ' (test mode)' : ''}`
                : 'Not connected'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`text-sm font-medium ${connected ? 'text-zinc-600' : 'text-emerald-600'}`}
          data-testid="stripe-toggle-btn"
        >
          {connected ? (expanded ? 'Done' : 'Update key') : (expanded ? 'Cancel' : 'Connect')}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-zinc-200 space-y-3">
          <div>
            <label className="block text-xs font-medium text-zinc-700 mb-1">
              Stripe Secret Key
            </label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="sk_live_..."
                className="w-full px-3 py-2 pr-10 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="stripe-secret-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700 p-1"
                data-testid="stripe-toggle-visibility"
              >
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Get it from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Stripe Dashboard → Developers → API keys</a>. Use the <strong>Secret key</strong> (starts with <code>sk_live_</code>).
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="stripe-error">
              {error}
            </div>
          )}

          <button
            onClick={handleConnect}
            disabled={saving || !secretKey.trim() || success}
            className="w-full py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="stripe-connect-btn"
          >
            {saving ? (<><Loader2 size={16} className="animate-spin" /> Validating…</>) : success ? (<><CheckCircle size={16} /> Connected!</>) : 'Save & Validate Key'}
          </button>

          <p className="text-xs text-zinc-500">
            <strong>Security:</strong> your secret key is stored encrypted and only used server-side when generating payment links or charging saved cards. It is never sent to the browser.
          </p>
        </div>
      )}
    </div>
  );
};




// -----------------------------------------------------------------------------
// DefaultServiceNameSetting
// One-field editor for `default_service_name`. Used as the fallback for the
// `{service}` token in SMS/email reminders when an individual job/customer
// doesn't have its own service specified (common for service businesses where
// every customer gets the same offering, e.g. window cleaning).
// -----------------------------------------------------------------------------
const DefaultServiceNameSetting = () => {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const r = await fetch(`${API_URL}/api/settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setValue(d.default_service_name || 'Window Clean');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const r = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_service_name: value.trim() || 'Window Clean' }),
      });
      if (r.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        alert('Failed to save default service name');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3" data-testid="default-service-name-setting">
      <div>
        <p className="font-medium text-zinc-900">Default Service Name</p>
        <p className="text-xs text-zinc-500 mt-0.5">
          Used in <code className="text-zinc-700">{'{service}'}</code> on SMS/email reminders when a specific job or customer doesn&apos;t have its own service set.
        </p>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={loading ? 'Loading…' : value}
          onChange={(e) => setValue(e.target.value)}
          disabled={loading || saving}
          placeholder="Window Clean"
          maxLength={60}
          className="flex-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="default-service-name-input"
        />
        <button
          onClick={save}
          disabled={loading || saving || !value.trim()}
          className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-2"
          data-testid="default-service-name-save"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : saved ? <CheckCircle size={16} /> : 'Save'}
        </button>
      </div>
    </div>
  );
};




// -----------------------------------------------------------------------------
// PaymentRemindersSection
// Edit the email/SMS templates sent from Owings → Request Payment. Persists
// to /api/payment-reminder-templates. Supports placeholders that the backend
// resolves at send time.
// -----------------------------------------------------------------------------
const PLACEHOLDER_TOKENS = [
  { key: '{first_name}', desc: "Customer's first name" },
  { key: '{name}', desc: "Customer's full name" },
  { key: '{company_name}', desc: 'Your company name' },
  { key: '{balance}', desc: 'Outstanding balance (e.g. 75.00)' },
  { key: '{pay_link}', desc: 'Stripe pay-now link' },
  { key: '{service}', desc: 'Service the customer received (e.g. Window Clean)' },
  { key: '{invoice_list}', desc: 'Bulleted list of unpaid invoices' },
];

const PaymentRemindersSection = () => {
  const [templates, setTemplates] = useState({ email_subject: '', email_body: '', sms_body: '' });
  const [defaults, setDefaults] = useState({ email_subject: '', email_body: '', sms_body: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeField, setActiveField] = useState('email_body');
  const fieldRefs = {
    email_subject: useRef(null),
    email_body: useRef(null),
    sms_body: useRef(null),
  };

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const r = await fetch(`${API_URL}/api/payment-reminder-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setTemplates({
          email_subject: d.email_subject || '',
          email_body: d.email_body || '',
          sms_body: d.sms_body || '',
        });
        setDefaults(d.defaults || {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const insertToken = (token) => {
    const ref = fieldRefs[activeField]?.current;
    if (!ref) return;
    const start = ref.selectionStart ?? templates[activeField].length;
    const end = ref.selectionEnd ?? templates[activeField].length;
    const current = templates[activeField] || '';
    const next = current.slice(0, start) + token + current.slice(end);
    setTemplates((prev) => ({ ...prev, [activeField]: next }));
    setTimeout(() => {
      ref.focus();
      ref.selectionStart = ref.selectionEnd = start + token.length;
    }, 0);
  };

  const resetToDefault = (field) => {
    setTemplates((prev) => ({ ...prev, [field]: defaults[field] || '' }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const r = await fetch(`${API_URL}/api/payment-reminder-templates`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(templates),
      });
      if (r.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        alert('Failed to save templates');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-8">
        <Loader2 size={18} className="animate-spin" /> Loading templates…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="payment-reminders-section">
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <p className="text-sm text-amber-900">
          <strong>What is this?</strong> When you tap <em>Request Payment</em> on the Owings page, this is the message that gets sent.
          Tap a placeholder chip below to insert it into whichever field you last focused.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {PLACEHOLDER_TOKENS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => insertToken(t.key)}
              title={t.desc}
              data-testid={`pr-token-${t.key.replace(/[{}]/g, '')}`}
              className="text-xs font-mono px-2 py-1 rounded-md bg-white text-amber-800 border border-amber-300 hover:bg-amber-100 active:scale-95 transition"
            >
              {t.key}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2"><Mail size={16} /> Email subject</h3>
          <button onClick={() => resetToDefault('email_subject')} className="text-xs text-zinc-500 hover:text-zinc-700">Reset</button>
        </div>
        <input
          ref={fieldRefs.email_subject}
          value={templates.email_subject}
          onFocus={() => setActiveField('email_subject')}
          onChange={(e) => setTemplates((p) => ({ ...p, email_subject: e.target.value }))}
          className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="pr-email-subject"
        />
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2"><Mail size={16} /> Email body</h3>
          <button onClick={() => resetToDefault('email_body')} className="text-xs text-zinc-500 hover:text-zinc-700">Reset</button>
        </div>
        <textarea
          ref={fieldRefs.email_body}
          value={templates.email_body}
          onFocus={() => setActiveField('email_body')}
          onChange={(e) => setTemplates((p) => ({ ...p, email_body: e.target.value }))}
          rows={10}
          className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="pr-email-body"
        />
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2"><MessageSquare size={16} /> SMS body</h3>
          <button onClick={() => resetToDefault('sms_body')} className="text-xs text-zinc-500 hover:text-zinc-700">Reset</button>
        </div>
        <textarea
          ref={fieldRefs.sms_body}
          value={templates.sms_body}
          onFocus={() => setActiveField('sms_body')}
          onChange={(e) => setTemplates((p) => ({ ...p, sms_body: e.target.value }))}
          rows={4}
          maxLength={320}
          className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="pr-sms-body"
        />
        <p className="text-xs text-zinc-500">{(templates.sms_body || '').length} / 320 chars • SMS over 160 chars = 2 credits</p>
      </div>

      <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-xl text-xs text-zinc-600 space-y-1">
        <p><strong>WhatsApp:</strong> uses the Meta-approved <code>payment_reminder</code> template — wording is fixed by Meta and can&apos;t be edited here. The pay-now button is appended automatically.</p>
        <p><strong>Pay link:</strong> goes to the customer&apos;s oldest unpaid invoice. Clicking opens a fresh Stripe Checkout — works even if older sessions have expired.</p>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="pr-save-btn"
      >
        {saving ? (<><Loader2 size={18} className="animate-spin" /> Saving…</>) : saved ? (<><CheckCircle size={18} /> Saved!</>) : 'Save Templates'}
      </button>
    </div>
  );
};



const Settings = ({ onNavigate, onMenuClick }) => {
  // Use theme context
  const { currentTheme, customColor, changeTheme, themeDefinitions } = useTheme();
  
  // Local state for UI (synced with context)
  const [selectedTheme, setSelectedTheme] = useState(currentTheme);
  const [customThemeColor, setCustomThemeColor] = useState(customColor);
  const [darkMode, setDarkMode] = useState(false);
  const [expandedSection, setExpandedSection] = useState('appearance');
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  
  // Navigation Settings state
  const [navigationApp, setNavigationApp] = useState('');
  const [navigationMode, setNavigationMode] = useState('address');
  // Route optimisation
  const [startingPostcode, setStartingPostcode] = useState('');
  const [startingLat, setStartingLat] = useState(null);
  const [startingLng, setStartingLng] = useState(null);
  const [savingStartingPostcode, setSavingStartingPostcode] = useState(false);
  const [startingPostcodeMessage, setStartingPostcodeMessage] = useState('');

  // Track which template field was last focused, so the variable chips can
  // insert at the cursor in that field. Stored in a ref so a focus change
  // doesn't cause a re-render.
  const templateFocusFieldRef = useRef(null);
  const handleTemplateFieldFocus = (key, eventOrEl) => {
    const el = eventOrEl?.target || eventOrEl;
    templateFocusFieldRef.current = { key, el };
  };
  const insertTemplateToken = (token) => {
    const focused = templateFocusFieldRef.current;
    if (!focused || !focused.el) {
      toast.info('Tap inside a message field first, then tap a token');
      return;
    }
    const { key, el } = focused;
    const start = el.selectionStart ?? (messagingTemplates[key]?.length || 0);
    const end = el.selectionEnd ?? start;
    const current = messagingTemplates[key] || '';
    const next = current.slice(0, start) + token + current.slice(end);
    setMessagingTemplates(prev => ({ ...prev, [key]: next }));
    // Restore caret position just after the inserted token
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch (e) { /* swallow */ }
    });
  };
  const [navSaving, setNavSaving] = useState(false);
  
  // VAT Settings state
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatPercentage, setVatPercentage] = useState(20);
  const [vatPricesIncludeVat, setVatPricesIncludeVat] = useState(true);
  const [vatBulkImportIncludesVat, setVatBulkImportIncludesVat] = useState(true);
  const [savingVat, setSavingVat] = useState(false);
  const [vatSaveMessage, setVatSaveMessage] = useState('');
  
  // Company Logo state
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [savingCompanyName, setSavingCompanyName] = useState(false);
  const [companyNameSaved, setCompanyNameSaved] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Logo usage settings
  const [logoSettings, setLogoSettings] = useState({
    map_pins_dashboard: true,
    map_pins_rounds: true,
    map_pins_daypilot: true,
    map_pins_customer_card: true,
    email_headers: true,
    invoice_headers: true
  });
  const [savingLogoSettings, setSavingLogoSettings] = useState(false);
  
  // Worker permissions state
  const [workerPermissions, setWorkerPermissions] = useState({
    can_see_prices: false,
    can_see_customer_balance: false,
    can_send_reminders: false,
    can_create_jobs: true,
    can_mark_jobs_done: true,
    can_skip_jobs: true,
    can_see_customer_phone: true,
    can_see_customer_address: true,
    can_take_payments: false
  });
  const [savingWorkerPermissions, setSavingWorkerPermissions] = useState(false);
  const [workerPermissionsSaved, setWorkerPermissionsSaved] = useState(false);
  
  // Integration status state
  const [integrations, setIntegrations] = useState({});
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookCopied, setWebhookCopied] = useState(false);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState(['read']);
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [keyError, setKeyError] = useState('');
  
  // Team users state - for displaying actual staff, not hardcoded
  const [teamUsers, setTeamUsers] = useState([]);
  const [loadingTeamUsers, setLoadingTeamUsers] = useState(true);
  
  // Add user modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('Worker');
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  
  // General settings state
  const [showGeneralEdit, setShowGeneralEdit] = useState(null); // 'currency', 'timezone', 'dateformat', 'address'
  const [generalSettings, setGeneralSettings] = useState({
    currency: 'GBP',
    timezone: 'Europe/London',
    dateFormat: 'DD/MM/YYYY',
    businessAddress: 'Burgess Hill, West Sussex'
  });
  const [savingGeneral, setSavingGeneral] = useState(false);
  
  // Handle adding a new user
  const handleAddUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) {
      setAddUserError('Name and email are required');
      return;
    }
    
    setAddingUser(true);
    setAddUserError('');
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/team/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newUserName.trim(),
          email: newUserEmail.trim(),
          password: newUserPassword || null,
          role: newUserRole
        })
      });
      
      if (res.ok) {
        const newUser = await res.json();
        setTeamUsers(prev => [...prev, newUser]);
        setShowAddUserModal(false);
        setNewUserName('');
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserRole('Worker');
        toast.success('User added successfully');
      } else {
        const error = await res.json();
        setAddUserError(error.detail || 'Failed to add user');
      }
    } catch (err) {
      console.error('Error adding user:', err);
      setAddUserError('Network error - please try again');
    } finally {
      setAddingUser(false);
    }
  };
  
  // Load team users on mount
  useEffect(() => {
    const loadTeamUsers = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/team/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTeamUsers(data.users || []);
        }
      } catch (err) {
        console.error('Error loading team users:', err);
      } finally {
        setLoadingTeamUsers(false);
      }
    };
    loadTeamUsers();
  }, []);
  
  // Load integrations status
  const loadIntegrations = useCallback(async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/integrations/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Handle connections as object (not array)
        const connections = data.connections || {};
        setIntegrations(connections);
        
        // Generate webhook URL based on current origin
        const baseUrl = window.location.origin.includes('localhost') 
          ? API_URL 
          : window.location.origin;
        setWebhookUrl(`${baseUrl}/api/webhook/gocardless`);
      }
    } catch (err) {
      console.error('Error loading integrations:', err);
    }
  }, []);

  useEffect(() => { loadIntegrations(); }, [loadIntegrations]);
  
  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setWebhookCopied(true);
    setTimeout(() => setWebhookCopied(false), 2000);
  };
  
  // Load API keys
  const loadApiKeys = async () => {
    setLoadingApiKeys(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Ensure we always set an array
        const keys = Array.isArray(data) ? data : (Array.isArray(data?.keys) ? data.keys : []);
        setApiKeys(keys);
      } else {
        setApiKeys([]);
      }
    } catch (err) {
      console.error('Error loading API keys:', err);
      setApiKeys([]);
    } finally {
      setLoadingApiKeys(false);
    }
  };
  
  // Create new API key
  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      setKeyError('Please enter a name for this key');
      return;
    }
    
    setCreatingKey(true);
    setKeyError('');
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newKeyName.trim(),
          scopes: newKeyScopes
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setNewlyCreatedKey(data.key); // Backend returns 'key', not 'api_key'
        setShowCreateKeyModal(false); // Close the modal so user can see the key warning
        setNewKeyName('');
        setNewKeyScopes(['read']);
        loadApiKeys(); // Refresh the list
        toast.success('API key created - copy it now!');
      } else {
        try {
          const error = await res.json();
          setKeyError(error.detail || 'Failed to create API key');
        } catch {
          setKeyError(`Server error (${res.status}) - please try again`);
        }
      }
    } catch (err) {
      console.error('Error creating API key:', err);
      setKeyError('Network error - please try again');
    } finally {
      setCreatingKey(false);
    }
  };
  
  // Revoke API key
  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        loadApiKeys();
        toast.success('API key revoked');
      } else {
        toast.error('Failed to revoke API key');
      }
    } catch (err) {
      console.error('Error revoking API key:', err);
      toast.error('Network error');
    }
  };
  
  // Copy API key to clipboard
  const copyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };
  
  // Load API keys when developer section is expanded
  useEffect(() => {
    const loadKeys = async () => {
      if (expandedSection === 'developer') {
        await loadApiKeys();
      }
    };
    loadKeys();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSection]);
  
  // Load worker permissions on mount
  useEffect(() => {
    const loadWorkerPermissions = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/worker-permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWorkerPermissions(data);
        }
      } catch (err) {
        console.error('Error loading worker permissions:', err);
      }
    };
    loadWorkerPermissions();
  }, []);
  
  // Save worker permissions
  const saveWorkerPermissions = async () => {
    setSavingWorkerPermissions(true);
    setWorkerPermissionsSaved(false);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/worker-permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(workerPermissions)
      });
      if (res.ok) {
        setWorkerPermissionsSaved(true);
        // Hide the saved message after 3 seconds
        setTimeout(() => setWorkerPermissionsSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving worker permissions:', err);
    } finally {
      setSavingWorkerPermissions(false);
    }
  };
  
  // Frequency settings
  const [defaultFrequency, setDefaultFrequency] = useState('6');
  const [customFrequencies, setCustomFrequencies] = useState([
    { value: '4', label: '4 Weekly' },
    { value: '6', label: '6 Weekly' },
    { value: '8', label: '8 Weekly' },
    { value: '12', label: '12 Weekly' }
  ]);
  const [newFrequencyValue, setNewFrequencyValue] = useState('');
  const [savingFrequency, setSavingFrequency] = useState(false);
  const [frequencySaveMessage, setFrequencySaveMessage] = useState('');
  
  // Invoice/Banking settings
  const [bankingSettings, setBankingSettings] = useState({
    business_address: '',
    business_email: '',
    business_phone: '',
    vat_number: '',
    bank_name: '',
    account_name: '',
    sort_code: '',
    account_number: ''
  });
  const [savingBanking, setSavingBanking] = useState(false);
  const [bankingSaveMessage, setBankingSaveMessage] = useState('');
  
  // Social & Review Links
  const [socialLinks, setSocialLinks] = useState({
    website_url: '',
    google_reviews_url: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    linkedin_url: '',
    tiktok_url: ''
  });
  const [savingSocial, setSavingSocial] = useState(false);
  const [socialSaveMessage, setSocialSaveMessage] = useState('');
  
  // Invoice/Email Settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoice_default_notes: '',
    email_signature: '',
    include_logo_in_signature: true,
    include_social_in_signature: true,
    auto_create_invoices: true,
    auto_send_invoices: true,  // Default to ON
    invoice_payment_days: 14,
    invoice_payment_terms: 'on_receipt', // "on_receipt" or "days"
    show_tax_on_invoices: true,
    invoice_show_card_payment: true,
    invoice_show_dd_payment: true,
    invoice_show_bacs: true,
    // Invoice numbering settings
    invoice_prefix: 'INV',
    invoice_next_number: 1,
    invoice_number_format: 'prefix-year-seq', // 'prefix-year-seq', 'prefix-seq', 'seq-only'
    // CC every outgoing invoice email to this address (e.g. bookings@, bookkeeper)
    cc_invoice_emails: '',
    invoice_email_bcc: '',
    // Forward incoming customer SMS replies to this mobile (so the user can
    // see replies on their phone in addition to the in-app Messages inbox).
    sms_forward_to: ''
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [invoiceSaveMessage, setInvoiceSaveMessage] = useState('');
  // Real sender email from backend (was previously hardcoded `noreply@perfectpanes.co.uk`
  // in the UI, which silently misled users — Render is actually using SENDER_EMAIL env
  // var which can differ).
  const [emailSenderInfo, setEmailSenderInfo] = useState(null);
  
  // Messaging Templates
  const [messagingTemplates, setMessagingTemplates] = useState({
    email_reminder_subject: 'Reminder: {service} Tomorrow - {business_name}',
    email_reminder_body: 'Hi {customer_name},\n\nThis is a friendly reminder that your {service} is scheduled for tomorrow.\n\nPlease ensure access is available.\n\nThanks,\n{business_name}',
    sms_reminder: 'Hi {customer_name}, reminder: your {service} is booked for tomorrow. Thanks - {business_name}',
    email_invoice_subject: 'Invoice #{invoice_number} from {business_name}',
    sms_invoice: 'Hi {customer_name}, your invoice for {service} (£{amount}) is ready. View: {invoice_link} - {business_name}'
  });
  const [savingMessaging, setSavingMessaging] = useState(false);
  const [messagingSaveMessage, setMessagingSaveMessage] = useState('');

  // Email Branding Color
  const [emailBrandColor, setEmailBrandColor] = useState('#10b981'); // Default emerald green
  const [customHexInput, setCustomHexInput] = useState('');
  const [savingBrandColor, setSavingBrandColor] = useState(false);
  const [brandColorSaveMessage, setBrandColorSaveMessage] = useState('');

  // Auto-send invoice on Done (when customer has no saved payment method)
  const [autoSendInvoice, setAutoSendInvoice] = useState(false);
  const [autoSendMethod, setAutoSendMethod] = useState('both'); // 'whatsapp_sms' | 'email' | 'both'

  // Handover Mode — when ON, completing a job rolls forward the schedule
  // but skips invoice creation, balance updates, charges, and all comms.
  // Used when the round has been sold to another company.
  const [handoverMode, setHandoverMode] = useState(false);
  const [plannerDisplayStyle, setPlannerDisplayStyle] = useState('classic');
  const [plannerBarColor, setPlannerBarColor] = useState('#10b981');

  // Preset brand colors
  const brandColorPresets = [
    { id: 'green', name: 'Green', color: '#10b981' },
    { id: 'blue', name: 'Blue', color: '#3b82f6' },
    { id: 'black', name: 'Black', color: '#18181b' },
    { id: 'orange', name: 'Orange', color: '#f97316' },
    { id: 'red', name: 'Red', color: '#ef4444' },
    { id: 'purple', name: 'Purple', color: '#8b5cf6' },
    { id: 'pink', name: 'Pink', color: '#ec4899' },
    { id: 'teal', name: 'Teal', color: '#14b8a6' },
  ];

  // Fetch user settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setVatEnabled(data.vat_enabled || false);
          setVatPercentage(data.vat_percentage || 20);
          setVatPricesIncludeVat(data.vat_prices_include_vat !== false);
          setVatBulkImportIncludesVat(data.vat_bulk_import_includes_vat !== false);
          setCompanyLogo(data.company_logo || '');
          setCompanyName(data.company_name || '');
          // Load logo usage settings
          if (data.logo_settings) {
            setLogoSettings(prev => ({ ...prev, ...data.logo_settings }));
          }
          // Load banking settings
          setBankingSettings({
            business_address: data.business_address || '',
            business_email: data.business_email || data.email || '',
            business_phone: data.business_phone || '',
            vat_number: data.vat_number || '',
            bank_name: data.bank_name || '',
            account_name: data.account_name || '',
            sort_code: data.sort_code || '',
            account_number: data.account_number || ''
          });
          // Load social links
          setSocialLinks({
            website_url: data.website_url || '',
            google_reviews_url: data.google_reviews_url || '',
            facebook_url: data.facebook_url || '',
            instagram_url: data.instagram_url || '',
            twitter_url: data.twitter_url || '',
            linkedin_url: data.linkedin_url || '',
            tiktok_url: data.tiktok_url || ''
          });
          // Load invoice settings
          setInvoiceSettings({
            invoice_default_notes: data.invoice_default_notes || '',
            email_signature: data.email_signature || '',
            include_logo_in_signature: data.include_logo_in_signature !== false,
            include_social_in_signature: data.include_social_in_signature !== false,
            auto_create_invoices: data.auto_create_invoices !== false,
            auto_send_invoices: data.auto_send_invoices !== false,  // Default to true
            invoice_payment_days: data.invoice_payment_days || 14,
            invoice_payment_terms: data.invoice_payment_terms || 'on_receipt',
            show_tax_on_invoices: data.show_tax_on_invoices !== false,
            invoice_show_card_payment: data.invoice_show_card_payment !== false,
            invoice_show_dd_payment: data.invoice_show_dd_payment !== false,
            invoice_show_bacs: data.invoice_show_bacs !== false,
            // Invoice numbering settings
            invoice_prefix: data.invoice_prefix || 'INV',
            invoice_next_number: data.invoice_next_number || 1,
            invoice_number_format: data.invoice_number_format || 'prefix-year-seq',
            // CC/BCC for outgoing invoice emails
            cc_invoice_emails: data.cc_invoice_emails || '',
            invoice_email_bcc: data.invoice_email_bcc || '',
            sms_forward_to: data.sms_forward_to || ''
          });
          // Load messaging templates
          setMessagingTemplates({
            email_reminder_subject: data.email_reminder_subject || 'Reminder: {service} Tomorrow - {business_name}',
            email_reminder_body: data.email_reminder_body || 'Hi {customer_name},\n\nThis is a friendly reminder that your {service} is scheduled for tomorrow.\n\nPlease ensure access is available.\n\nThanks,\n{business_name}',
            sms_reminder: data.sms_reminder || 'Hi {customer_name}, reminder: your {service} is booked for tomorrow. Thanks - {business_name}',
            email_invoice_subject: data.email_invoice_subject || 'Invoice #{invoice_number} from {business_name}',
            sms_invoice: data.sms_invoice || 'Hi {customer_name}, your invoice for {service} (£{amount}) is ready. View: {invoice_link} - {business_name}'
          });
          // Load email brand color
          if (data.email_brand_color) {
            setEmailBrandColor(data.email_brand_color);
          }
          // Load navigation settings
          if (data.navigation_app) {
            setNavigationApp(data.navigation_app);
          }
          if (data.navigation_mode) {
            setNavigationMode(data.navigation_mode);
          }
          // Load route optimisation starting postcode
          setStartingPostcode(data.starting_postcode || '');
          setStartingLat(data.starting_lat ?? null);
          setStartingLng(data.starting_lng ?? null);
          // Load auto-send invoice on Done settings
          setAutoSendInvoice(!!data.auto_send_invoice_no_payment);
          // Backend stores 'sms' for "WhatsApp + SMS fallback". Map back for UI.
          setAutoSendMethod(
            data.auto_send_invoice_method === 'sms'
              ? 'whatsapp_sms'
              : (data.auto_send_invoice_method || 'both')
          );
          // Load Handover Mode toggle
          setHandoverMode(!!data.handover_mode);
          // Load Work Planner display style
          setPlannerDisplayStyle(data.work_planner_display || 'classic');
          setPlannerBarColor(data.work_planner_bar_color || '#10b981');
          // Load frequency settings
          if (data.default_frequency) {
            setDefaultFrequency(data.default_frequency);
          }
          if (data.custom_frequencies && Array.isArray(data.custom_frequencies)) {
            setCustomFrequencies(data.custom_frequencies);
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
    
    // Fetch the real `From:` address used on outgoing emails (server-side
    // Resend config). Surfaced in the Notifications tab so the displayed
    // email address matches what customers actually see in their inbox.
    const fetchEmailSender = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/settings/email-sender`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setEmailSenderInfo(await res.json());
        }
      } catch (e) {
        // Non-fatal — UI just falls back to a "—" placeholder.
        console.warn('Email sender fetch failed', e);
      }
    };
    fetchEmailSender();
  }, []);
  
  // Save messaging templates
  const saveMessagingTemplates = async () => {
    setSavingMessaging(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messagingTemplates)
      });
      if (response.ok) {
        setMessagingSaveMessage('Saved!');
        setTimeout(() => setMessagingSaveMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error saving messaging templates:', error);
    } finally {
      setSavingMessaging(false);
    }
  };

  // Save email brand color
  const saveEmailBrandColor = async (color) => {
    setSavingBrandColor(true);
    setBrandColorSaveMessage('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email_brand_color: color })
      });
      if (response.ok) {
        setEmailBrandColor(color);
        setBrandColorSaveMessage('Saved!');
        setTimeout(() => setBrandColorSaveMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error saving email brand color:', error);
    } finally {
      setSavingBrandColor(false);
    }
  };

  // Handle custom hex input
  const handleCustomHexSubmit = () => {
    let hex = customHexInput.trim();
    // Add # if missing
    if (hex && !hex.startsWith('#')) {
      hex = '#' + hex;
    }
    // Validate hex format
    if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)) {
      saveEmailBrandColor(hex);
      setCustomHexInput('');
    } else {
      alert('Please enter a valid hex color (e.g., #3b82f6 or 3b82f6)');
    }
  };

  // Save frequency settings
  const saveFrequencySettings = async (newDefault = defaultFrequency, newFrequencies = customFrequencies) => {
    setSavingFrequency(true);
    setFrequencySaveMessage('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          default_frequency: newDefault,
          custom_frequencies: newFrequencies
        })
      });
      if (response.ok) {
        setFrequencySaveMessage('Saved!');
        setTimeout(() => setFrequencySaveMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error saving frequency settings:', error);
    } finally {
      setSavingFrequency(false);
    }
  };

  // Add custom frequency
  const handleAddFrequency = () => {
    const value = newFrequencyValue.trim();
    if (!value || isNaN(parseInt(value))) {
      alert('Please enter a valid number of weeks');
      return;
    }
    const numValue = value.replace(/\D/g, '');
    if (customFrequencies.some(f => f.value === numValue)) {
      alert('This frequency already exists');
      return;
    }
    const newFreq = { value: numValue, label: `${numValue} Weekly` };
    const newList = [...customFrequencies, newFreq].sort((a, b) => parseInt(a.value) - parseInt(b.value));
    setCustomFrequencies(newList);
    setNewFrequencyValue('');
    saveFrequencySettings(defaultFrequency, newList);
  };

  // Remove custom frequency
  const handleRemoveFrequency = (valueToRemove) => {
    // Don't remove the default frequency
    if (valueToRemove === defaultFrequency) {
      alert('Cannot remove the default frequency. Please change the default first.');
      return;
    }
    const newList = customFrequencies.filter(f => f.value !== valueToRemove);
    setCustomFrequencies(newList);
    saveFrequencySettings(defaultFrequency, newList);
  };

  // Change default frequency
  const handleDefaultFrequencyChange = (newDefault) => {
    setDefaultFrequency(newDefault);
    saveFrequencySettings(newDefault, customFrequencies);
  };
  
  // Save logo usage settings
  const saveLogoSettings = async (newSettings) => {
    setSavingLogoSettings(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ logo_settings: newSettings })
      });
    } catch (error) {
      console.error('Error saving logo settings:', error);
    } finally {
      setSavingLogoSettings(false);
    }
  };
  
  const handleLogoSettingChange = (key, value) => {
    const newSettings = { ...logoSettings, [key]: value };
    setLogoSettings(newSettings);
    saveLogoSettings(newSettings);
  };

  // Save company name
  const saveCompanyName = async () => {
    setSavingCompanyName(true);
    setCompanyNameSaved(false);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ company_name: companyName })
      });
      if (response.ok) {
        setCompanyNameSaved(true);
        setTimeout(() => setCompanyNameSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving company name:', error);
    } finally {
      setSavingCompanyName(false);
    }
  };

  // Save VAT settings
  const saveVatSettings = async (enabled, percentage, pricesIncludeVat, bulkIncludesVat) => {
    setSavingVat(true);
    setVatSaveMessage('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vat_enabled: enabled,
          vat_percentage: percentage,
          vat_prices_include_vat: pricesIncludeVat,
          vat_bulk_import_includes_vat: bulkIncludesVat
        })
      });
      if (response.ok) {
        setVatSaveMessage('Settings saved!');
        setTimeout(() => setVatSaveMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error saving VAT settings:', error);
      setVatSaveMessage('Error saving');
    } finally {
      setSavingVat(false);
    }
  };

  const handleVatToggle = (checked) => {
    setVatEnabled(checked);
    saveVatSettings(checked, vatPercentage, vatPricesIncludeVat, vatBulkImportIncludesVat);
  };

  const handleVatPercentageChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setVatPercentage(numValue);
  };

  const handleVatPercentageBlur = () => {
    // Save when user finishes editing
    if (vatEnabled) {
      saveVatSettings(vatEnabled, vatPercentage, vatPricesIncludeVat, vatBulkImportIncludesVat);
    }
  };

  const handleVatPricesIncludeVatChange = (checked) => {
    setVatPricesIncludeVat(checked);
    if (vatEnabled) {
      saveVatSettings(vatEnabled, vatPercentage, checked, vatBulkImportIncludesVat);
    }
  };

  const handleVatBulkImportIncludesVatChange = (checked) => {
    setVatBulkImportIncludesVat(checked);
    if (vatEnabled) {
      saveVatSettings(vatEnabled, vatPercentage, vatPricesIncludeVat, checked);
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, GIF, WebP, or SVG file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('File too large. Max size is 2MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/settings/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanyLogo(data.logo_url);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove your company logo?')) return;
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings/logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setCompanyLogo('');
      }
    } catch (error) {
      console.error('Error removing logo:', error);
    }
  };
  
  // Save banking/invoice settings
  const saveBankingSettings = async () => {
    setSavingBanking(true);
    setBankingSaveMessage('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bankingSettings)
      });
      
      if (response.ok) {
        setBankingSaveMessage('Saved!');
        setTimeout(() => setBankingSaveMessage(''), 2000);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving banking settings:', error);
      alert('Error saving settings');
    } finally {
      setSavingBanking(false);
    }
  };
  
  // Save auto-send invoice on Done toggle (debounced via fire-and-forget)
  const saveAutoSendInvoice = async (enabled, method) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          auto_send_invoice_no_payment: enabled,
          auto_send_invoice_method: method
        })
      });
    } catch (error) {
      console.error('Error saving auto-send invoice settings:', error);
    }
  };

  // Save Work Planner display style (synced to the account)
  const savePlannerDisplay = async (style) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ work_planner_display: style })
      });
      if (!response.ok) {
        alert('Failed to save Work Planner display style');
      }
    } catch (error) {
      console.error('Error saving planner display style:', error);
    }
  };

  // Save Work Planner bar colour (synced to the account)
  const savePlannerBarColor = async (color) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ work_planner_bar_color: color })
      });
      if (!response.ok) {
        alert('Failed to save bar colour');
      }
    } catch (error) {
      console.error('Error saving planner bar colour:', error);
    }
  };

  // Save Handover Mode toggle
  const saveHandoverMode = async (enabled) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ handover_mode: enabled })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Failed to save Handover Mode');
        // Revert UI on failure
        setHandoverMode(!enabled);
      }
    } catch (error) {
      console.error('Error saving handover mode:', error);
      setHandoverMode(!enabled);
    }
  };

  // Save social links
  const saveSocialLinks = async () => {
    setSavingSocial(true);
    setSocialSaveMessage('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(socialLinks)
      });
      
      if (response.ok) {
        setSocialSaveMessage('Saved!');
        setTimeout(() => setSocialSaveMessage(''), 2000);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving social links:', error);
      alert('Error saving settings');
    } finally {
      setSavingSocial(false);
    }
  };
  
  // Save invoice settings
  const saveInvoiceSettings = async () => {
    setSavingInvoice(true);
    setInvoiceSaveMessage('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invoiceSettings)
      });
      
      if (response.ok) {
        setInvoiceSaveMessage('Saved!');
        setTimeout(() => setInvoiceSaveMessage(''), 2000);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving invoice settings:', error);
      alert('Error saving settings');
    } finally {
      setSavingInvoice(false);
    }
  };
  
  const themeColors = [
    { id: 'standard', name: 'Slate', color: '#0f172a', bgClass: 'bg-zinc-900' },
    { id: 'blue', name: 'Blue', color: '#3b82f6', bgClass: 'bg-blue-500' },
    { id: 'green', name: 'Green', color: '#22c55e', bgClass: 'bg-emerald-500' },
    { id: 'orange', name: 'Orange', color: '#f97316', bgClass: 'bg-orange-500' },
    { id: 'purple', name: 'Purple', color: '#8b5cf6', bgClass: 'bg-purple-500' },
    { id: 'pink', name: 'Pink', color: '#ec4899', bgClass: 'bg-pink-500' },
    { id: 'red', name: 'Red', color: '#ef4444', bgClass: 'bg-red-500' },
  ];

  // Sync local state with context on mount
  useEffect(() => {
    setSelectedTheme(currentTheme);
    setCustomThemeColor(customColor);
  }, [currentTheme, customColor]);

  // Handle theme selection with save
  const handleThemeSelect = async (themeId) => {
    setSelectedTheme(themeId);
    setThemeSaving(true);
    try {
      await changeTheme(themeId, themeId === 'custom' ? customThemeColor : null);
      toast.success(`Theme changed to ${themeColors.find(t => t.id === themeId)?.name || themeId}`);
    } catch (err) {
      toast.error('Failed to save theme');
    } finally {
      setThemeSaving(false);
    }
  };

  // Handle custom color change
  const handleCustomColorApply = async () => {
    setSelectedTheme('custom');
    setThemeSaving(true);
    try {
      await changeTheme('custom', customThemeColor);
      toast.success('Custom theme applied!');
    } catch (err) {
      toast.error('Failed to save theme');
    } finally {
      setThemeSaving(false);
    }
  };

  // Save navigation settings
  const saveNavigationSettings = async (app, mode) => {
    setNavSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          navigation_app: app,
          navigation_mode: mode
        })
      });
      setNavigationApp(app);
      setNavigationMode(mode);
      toast.success('Navigation preference saved!');
    } catch (err) {
      toast.error('Failed to save navigation settings');
    } finally {
      setNavSaving(false);
    }
  };

  // Clear navigation preference
  const clearNavigationPreference = async () => {
    setNavSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          navigation_app: '',
          navigation_mode: 'address'
        })
      });
      setNavigationApp('');
      setNavigationMode('address');
      toast.success('Navigation preference cleared - you will be asked each time');
    } catch (err) {
      toast.error('Failed to clear navigation settings');
    } finally {
      setNavSaving(false);
    }
  };

  // Save the route-optimisation starting postcode. Server geocodes it on save
  // and uses the resulting lat/lng as the start point for nearest-neighbour
  // routing on Work Planner.
  const saveStartingPostcode = async () => {
    setSavingStartingPostcode(true);
    setStartingPostcodeMessage('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ starting_postcode: startingPostcode.trim() })
      });
      if (!res.ok) {
        throw new Error('Failed');
      }
      const result = await res.json();
      const updated = result.updated || {};
      const lat = updated.starting_lat ?? null;
      const lng = updated.starting_lng ?? null;
      setStartingLat(lat);
      setStartingLng(lng);
      if (startingPostcode.trim() && lat == null) {
        setStartingPostcodeMessage('Saved, but we couldn\'t geocode that postcode — route optimisation will use the day\'s centre instead.');
        toast.warning('Could not locate that postcode');
      } else if (lat != null) {
        setStartingPostcodeMessage(`✓ Saved — route starts from ${startingPostcode.toUpperCase()}`);
        toast.success('Starting postcode saved');
      } else {
        setStartingPostcodeMessage('Cleared');
        toast.success('Starting postcode cleared');
      }
    } catch (err) {
      toast.error('Failed to save starting postcode');
    } finally {
      setSavingStartingPostcode(false);
    }
  };

  const settingsSections = [
    { id: 'subscription', name: 'Subscription', icon: Crown, description: 'Your plan and billing' },
    { id: 'setup-guide', name: 'Setup Guide', icon: Rocket, description: 'Replay the guided tour with voice & screenshots', isAction: true },
    { id: 'branding', name: 'Branding', icon: Image, description: 'Logo and company identity' },
    { id: 'general', name: 'General', icon: Building2, description: 'Company info, timezone' },
    { id: 'appearance', name: 'Appearance', icon: Palette, description: 'Theme and display' },
    { id: 'navigation', name: 'Navigation', icon: MapPin, description: 'Map app preferences' },
    { id: 'social', name: 'Social & Reviews', icon: Globe, description: 'Social links, Google reviews' },
    { id: 'sms', name: 'SMS Credits', icon: MessageSquare, description: 'Buy credits, sender ID', isLink: true, linkPath: '/settings/sms' },
    { id: 'messaging', name: 'Messaging', icon: Mail, description: 'Email & SMS templates + payment reminders' },
    { id: 'email-assistant', name: 'Email Assistant', icon: MessageCircle, description: 'Auto-reply to "when is my next clean?"' },
    { id: 'claude-connector', name: 'Claude Connector', icon: Sparkles, description: 'Ask Claude about your business (MCP)' },
    { id: 'notifications', name: 'Notifications', icon: Bell, description: 'Email, push settings' },
    { id: 'jobs', name: 'Jobs', icon: FileText, description: 'Default durations' },
    { id: 'invoicing', name: 'Invoicing', icon: CreditCard, description: 'Templates, terms, banking' },
    { id: 'payment-reminders', name: 'Payment Reminders', icon: AlertTriangle, description: 'Edit Owings "Request Payment" templates' },
    { id: 'users', name: 'Users', icon: Users, description: 'Staff accounts' },
    { id: 'worker-permissions', name: 'Worker Permissions', icon: Shield, description: 'What workers can see/do' },
    { id: 'integrations', name: 'Integrations', icon: Globe, description: 'Connected apps' },
    { id: 'developer', name: 'Developer & API', icon: Code, description: 'API keys, webhooks' },
    { id: 'data-export', name: 'Data Export', icon: Database, description: 'Download your data as CSV' },
    { id: 'security', name: 'Security', icon: Shield, description: 'Password, 2FA' },
    { id: 'mobile', name: 'Mobile', icon: Smartphone, description: 'App settings' },
  ];

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'subscription':
        return (
          <div className="space-y-4">
            {/* Current Plan Card */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Sparkles size={24} />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Current Plan</p>
                    <h3 className="text-2xl font-bold">Starter</h3>
                  </div>
                </div>
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  FREE (Beta)
                </span>
              </div>
              <p className="text-white/80 text-sm mb-4">
                You&apos;re on the Starter plan. Upgrade anytime to unlock more features!
              </p>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Check size={16} />
                <span>All features unlocked during beta</span>
              </div>
            </div>

            {/* Plan Features */}
            <div className="bg-white rounded-xl p-6 border border-zinc-200">
              <h4 className="font-semibold text-zinc-900 mb-4">What&apos;s included:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  'Unlimited Customers',
                  'Job Management',
                  'Invoicing & Payments',
                  'Route Optimisation',
                  'Email & SMS Messaging',
                  'Team Management',
                  'Financial Reports',
                  'Google Maps Integration'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                    <Check size={16} className="text-emerald-500" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Upgrade CTA */}
            <div className="bg-zinc-50 rounded-xl p-6 border border-zinc-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-zinc-900">View All Plans</h4>
                  <p className="text-sm text-zinc-500">Compare features and choose the best plan for your business</p>
                </div>
                <button
                  onClick={() => {
                    if (typeof onNavigate === 'function') {
                      onNavigate('pricing');
                    } else {
                      window.location.hash = '#pricing';
                    }
                  }}
                  className="px-4 py-2 bg-zinc-900 text-white rounded-xl font-medium hover:bg-zinc-800 flex items-center gap-2"
                >
                  View Plans <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Billing Info */}
            <div className="bg-white rounded-xl p-6 border border-zinc-200">
              <h4 className="font-semibold text-zinc-900 mb-4">Billing Information</h4>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Next billing date</span>
                  <span className="text-zinc-900 font-medium">Free during beta</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Payment method</span>
                  <span className="text-zinc-900 font-medium">Not required</span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'branding':
        return (
          <div className="space-y-6">
            {/* Company Logo Upload */}
            <div className="p-6 bg-zinc-50 rounded-xl">
              <p className="font-medium text-zinc-900 mb-4">Company Logo</p>
              <div className="flex items-start gap-6">
                {/* Logo Preview */}
                <div className="relative">
                  {companyLogo ? (
                    <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-zinc-200 bg-white flex items-center justify-center">
                      <img 
                        src={companyLogo} 
                        alt="Company Logo" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-100 flex flex-col items-center justify-center text-zinc-400">
                      <Image size={32} />
                      <span className="text-xs mt-1">No logo</span>
                    </div>
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1">
                  <p className="text-sm text-zinc-600 mb-3">
                    Upload your company logo. It will appear in the sidebar, invoices, and customer communications.
                  </p>
                  <div className="flex gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={16} />
                          Upload Logo
                        </>
                      )}
                    </button>
                    {companyLogo && (
                      <button
                        onClick={handleRemoveLogo}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors"
                        data-testid="remove-logo-btn"
                      >
                        <Trash2 size={16} />
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400 mt-2">
                    PNG, JPG, GIF, WebP, or SVG. Max 2MB.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Company Name */}
            <div className="p-4 bg-zinc-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-zinc-900">Company Name</p>
                {companyNameSaved && (
                  <span className="flex items-center gap-1 text-sm text-emerald-600 font-medium">
                    <Check size={14} /> Saved!
                  </span>
                )}
              </div>
              <p className="text-sm text-zinc-500 mb-3">This will appear on invoices and communications</p>
              <div className="flex gap-2 max-w-md">
                <Input 
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company Name"
                  className="flex-1"
                  data-testid="company-name-input"
                />
                <button
                  onClick={saveCompanyName}
                  disabled={savingCompanyName}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                  data-testid="save-company-name-btn"
                >
                  {savingCompanyName ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Save
                </button>
              </div>
            </div>

            {/* Logo Usage Settings */}
            {companyLogo && (
              <div className="p-6 bg-zinc-50 rounded-xl">
                <p className="font-medium text-zinc-900 mb-4">Logo Usage</p>
                <p className="text-sm text-zinc-600 mb-4">
                  Choose where your company logo appears throughout the application.
                </p>
                
                <div className="space-y-4">
                  {/* Map Pins Section */}
                  <div>
                    <p className="text-sm font-medium text-zinc-700 mb-3">Map Pins</p>
                    <div className="space-y-3 ml-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-900">Dashboard Map</p>
                          <p className="text-xs text-zinc-500">Show logo on map pins in main dashboard</p>
                        </div>
                        <Switch
                          checked={logoSettings.map_pins_dashboard}
                          onCheckedChange={(checked) => handleLogoSettingChange('map_pins_dashboard', checked)}
                          disabled={savingLogoSettings}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-900">Rounds View</p>
                          <p className="text-xs text-zinc-500">Show logo on map pins in rounds planning</p>
                        </div>
                        <Switch
                          checked={logoSettings.map_pins_rounds}
                          onCheckedChange={(checked) => handleLogoSettingChange('map_pins_rounds', checked)}
                          disabled={savingLogoSettings}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-900">Day Pilot Calendar</p>
                          <p className="text-xs text-zinc-500">Show logo on calendar map pins</p>
                        </div>
                        <Switch
                          checked={logoSettings.map_pins_daypilot}
                          onCheckedChange={(checked) => handleLogoSettingChange('map_pins_daypilot', checked)}
                          disabled={savingLogoSettings}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-900">Customer Cards</p>
                          <p className="text-xs text-zinc-500">Show logo on customer detail map pins</p>
                        </div>
                        <Switch
                          checked={logoSettings.map_pins_customer_card}
                          onCheckedChange={(checked) => handleLogoSettingChange('map_pins_customer_card', checked)}
                          disabled={savingLogoSettings}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Communications Section */}
                  <div className="pt-4 border-t border-zinc-200">
                    <p className="text-sm font-medium text-zinc-700 mb-3">Communications</p>
                    <div className="space-y-3 ml-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-900">Email Headers</p>
                          <p className="text-xs text-zinc-500">Include logo in email templates and notifications</p>
                        </div>
                        <Switch
                          checked={logoSettings.email_headers}
                          onCheckedChange={(checked) => handleLogoSettingChange('email_headers', checked)}
                          disabled={savingLogoSettings}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-900">Invoice Headers</p>
                          <p className="text-xs text-zinc-500">Show logo at the top of generated invoices</p>
                        </div>
                        <Switch
                          checked={logoSettings.invoice_headers}
                          onCheckedChange={(checked) => handleLogoSettingChange('invoice_headers', checked)}
                          disabled={savingLogoSettings}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {savingLogoSettings && (
                  <div className="flex items-center gap-2 mt-4 text-sm text-zinc-600">
                    <Loader2 size={14} className="animate-spin" />
                    Saving preferences...
                  </div>
                )}
              </div>
            )}

            {/* Email Brand Color */}
            <div className="p-6 bg-zinc-50 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium text-zinc-900">Email Brand Colour</p>
                  <p className="text-sm text-zinc-500">Customise headers and buttons in your emails and invoices</p>
                </div>
                {brandColorSaveMessage && (
                  <span className="flex items-center gap-1 text-sm text-emerald-600 font-medium">
                    <Check size={14} /> {brandColorSaveMessage}
                  </span>
                )}
              </div>

              {/* Current color preview */}
              <div className="flex items-center gap-4 mb-4 p-4 bg-white rounded-lg border border-zinc-200">
                <div 
                  className="w-12 h-12 rounded-lg shadow-sm border border-zinc-200 flex-shrink-0"
                  style={{ backgroundColor: emailBrandColor }}
                />
                <div>
                  <p className="text-sm font-medium text-zinc-900">Current Colour</p>
                  <p className="text-sm text-zinc-500 font-mono">{emailBrandColor}</p>
                </div>
              </div>

              {/* Preset colors */}
              <p className="text-sm font-medium text-zinc-700 mb-3">Choose a preset colour:</p>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 mb-6">
                {brandColorPresets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => saveEmailBrandColor(preset.color)}
                    disabled={savingBrandColor}
                    className={`relative group flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${
                      emailBrandColor === preset.color 
                        ? 'border-zinc-900 bg-zinc-100' 
                        : 'border-transparent hover:border-zinc-300 hover:bg-zinc-100'
                    }`}
                    data-testid={`brand-color-${preset.id}`}
                  >
                    <div 
                      className="w-8 h-8 rounded-full shadow-sm border border-zinc-200"
                      style={{ backgroundColor: preset.color }}
                    />
                    <span className="text-xs text-zinc-600">{preset.name}</span>
                    {emailBrandColor === preset.color && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-zinc-900 rounded-full flex items-center justify-center">
                        <Check size={10} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Custom hex input */}
              <div className="pt-4 border-t border-zinc-200">
                <p className="text-sm font-medium text-zinc-700 mb-3">Or enter a custom hex code:</p>
                <div className="flex gap-2 max-w-md">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-mono">#</span>
                    <Input 
                      value={customHexInput.replace('#', '')}
                      onChange={(e) => setCustomHexInput(e.target.value.replace('#', ''))}
                      placeholder="e.g., 3b82f6"
                      maxLength={6}
                      className="pl-7 font-mono"
                      data-testid="custom-hex-input"
                    />
                  </div>
                  <button
                    onClick={handleCustomHexSubmit}
                    disabled={savingBrandColor || !customHexInput}
                    className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-300 text-white text-sm font-medium rounded-lg transition-colors"
                    data-testid="apply-custom-hex-btn"
                  >
                    {savingBrandColor ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
                  </button>
                </div>
                <p className="text-xs text-zinc-400 mt-2">
                  Enter a 6-digit hex code without the # symbol (e.g., 3b82f6 for blue)
                </p>
              </div>

              {/* Email Preview */}
              <div className="mt-6 pt-4 border-t border-zinc-200">
                <p className="text-sm font-medium text-zinc-700 mb-3">Preview:</p>
                <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden shadow-sm">
                  <div 
                    className="p-4 text-center"
                    style={{ background: `linear-gradient(135deg, ${emailBrandColor}, ${adjustColor(emailBrandColor, -20)})` }}
                  >
                    <p className="text-white font-semibold text-lg">{companyName || 'Your Company'}</p>
                    <p className="text-white/80 text-sm">Invoice #12345</p>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-zinc-600 text-sm mb-3">Amount Due: <span className="font-bold text-lg" style={{ color: emailBrandColor }}>£50.00</span></p>
                    <button 
                      className="px-6 py-2 text-white text-sm font-medium rounded-lg"
                      style={{ backgroundColor: emailBrandColor }}
                    >
                      View & Pay Invoice
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'general':
        return (
          <div className="space-y-4">
            <SettingRow 
              title="Company Name" 
              subtitle={companyName || "Not set"} 
              actionText="Edit"
              onAction={() => setExpandedSection('branding')}
            />
            <SettingRow 
              title="Currency" 
              subtitle={`${generalSettings.currency} (${generalSettings.currency === 'GBP' ? '£' : generalSettings.currency === 'USD' ? '$' : '€'})`}
              actionText="Change" 
              onAction={() => setShowGeneralEdit('currency')}
            />
            <SettingRow 
              title="Time Zone" 
              subtitle={`${generalSettings.timezone} (${generalSettings.timezone === 'Europe/London' ? 'GMT+0' : 'GMT'})`}
              actionText="Change" 
              onAction={() => setShowGeneralEdit('timezone')}
            />
            <SettingRow 
              title="Date Format" 
              subtitle={generalSettings.dateFormat}
              actionText="Change" 
              onAction={() => setShowGeneralEdit('dateformat')}
            />
            <SettingRow 
              title="Business Address" 
              subtitle={generalSettings.businessAddress || "Not set"}
              actionText="Edit"
              onAction={() => setShowGeneralEdit('address')}
            />
            
            {/* Job Frequency Settings */}
            <div className="p-6 bg-zinc-50 rounded-xl mt-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium text-zinc-900">Job Frequency Settings</p>
                  <p className="text-sm text-zinc-500">Manage recurring job frequencies</p>
                </div>
                {frequencySaveMessage && (
                  <span className="flex items-center gap-1 text-sm text-emerald-600 font-medium">
                    <Check size={14} /> {frequencySaveMessage}
                  </span>
                )}
              </div>

              {/* Default Frequency */}
              <div className="mb-6">
                <label className="text-sm font-medium text-zinc-700 mb-2 block">
                  Default Frequency (for new jobs)
                </label>
                <select
                  value={defaultFrequency}
                  onChange={(e) => handleDefaultFrequencyChange(e.target.value)}
                  disabled={savingFrequency}
                  className="w-full max-w-xs px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                  data-testid="default-frequency-select"
                >
                  {customFrequencies.map((freq) => (
                    <option key={freq.value} value={freq.value}>{freq.label}</option>
                  ))}
                  <option value="">One-off (no repeat)</option>
                </select>
                <p className="text-xs text-zinc-500 mt-1">This will auto-apply when creating new jobs</p>
              </div>

              {/* Custom Frequencies List */}
              <div className="mb-4">
                <label className="text-sm font-medium text-zinc-700 mb-2 block">
                  Available Frequencies
                </label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {customFrequencies.map((freq) => (
                    <div 
                      key={freq.value}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                        freq.value === defaultFrequency 
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                          : 'bg-white border-zinc-200 text-zinc-700'
                      }`}
                    >
                      <span className="text-sm font-medium">{freq.label}</span>
                      {freq.value === defaultFrequency && (
                        <span className="text-xs bg-emerald-100 px-1.5 py-0.5 rounded">Default</span>
                      )}
                      {freq.value !== defaultFrequency && (
                        <button
                          onClick={() => handleRemoveFrequency(freq.value)}
                          className="text-zinc-400 hover:text-red-500 transition-colors"
                          title="Remove frequency"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <div 
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700"
                  >
                    <span className="text-sm font-medium">One-off</span>
                    <span className="text-xs">(always available)</span>
                  </div>
                </div>
              </div>

              {/* Add New Frequency */}
              <div className="pt-4 border-t border-zinc-200">
                <label className="text-sm font-medium text-zinc-700 mb-2 block">
                  Add Custom Frequency
                </label>
                <div className="flex gap-2 max-w-md">
                  <div className="flex-1 flex items-center gap-2">
                    <Input 
                      type="number"
                      min="1"
                      max="52"
                      value={newFrequencyValue}
                      onChange={(e) => setNewFrequencyValue(e.target.value)}
                      placeholder="e.g., 8"
                      className="w-24"
                      data-testid="new-frequency-input"
                    />
                    <span className="text-sm text-zinc-500">Weekly</span>
                  </div>
                  <button
                    onClick={handleAddFrequency}
                    disabled={savingFrequency || !newFrequencyValue}
                    className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-300 text-white text-sm font-medium rounded-lg transition-colors"
                    data-testid="add-frequency-btn"
                  >
                    {savingFrequency ? <Loader2 size={16} className="animate-spin" /> : 'Add'}
                  </button>
                </div>
                <p className="text-xs text-zinc-400 mt-2">
                  Enter number of weeks between services (e.g., 8 for every 8 weeks)
                </p>
              </div>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-6">
            {/* Work Planner Display style — synced to the account across devices */}
            <div data-testid="planner-display-section">
              <p className="font-medium text-zinc-900 mb-1">Work Planner Display</p>
              <p className="text-sm text-zinc-500 mb-3">Choose how the Work Planner looks — applies on all your devices</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'classic', label: 'Classic', desc: 'Day tiles + stat boxes' },
                  { id: 'chart', label: 'Chart', desc: 'Week bar chart + progress strip' },
                  { id: 'compact', label: 'Compact', desc: 'Chart header + dense job list' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => { setPlannerDisplayStyle(opt.id); savePlannerDisplay(opt.id); }}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      plannerDisplayStyle === opt.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-zinc-200 hover:border-zinc-300 bg-white'
                    }`}
                    data-testid={`planner-display-${opt.id}`}
                  >
                    <p className="font-semibold text-sm text-zinc-900">{opt.label}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 leading-tight">{opt.desc}</p>
                  </button>
                ))}
              </div>
              <p className="font-medium text-zinc-900 mt-4 mb-1">Planner Bar Colour</p>
              <p className="text-sm text-zinc-500 mb-2">Colour of the "done" fill in the day bars (skipped is always yellow, overdue always red)</p>
              <div className="flex gap-2 flex-wrap" data-testid="planner-bar-color-row">
                {[
                  { name: 'Green', hex: '#10b981' },
                  { name: 'Blue', hex: '#3b82f6' },
                  { name: 'Teal', hex: '#14b8a6' },
                  { name: 'Purple', hex: '#8b5cf6' },
                  { name: 'Indigo', hex: '#6366f1' },
                  { name: 'Orange', hex: '#f97316' },
                  { name: 'Pink', hex: '#ec4899' },
                  { name: 'Slate', hex: '#64748b' },
                ].map(c => (
                  <button
                    key={c.hex}
                    onClick={() => { setPlannerBarColor(c.hex); savePlannerBarColor(c.hex); }}
                    title={c.name}
                    className={`w-9 h-9 rounded-xl transition-all flex items-center justify-center ${
                      plannerBarColor === c.hex ? 'ring-2 ring-offset-2 ring-zinc-900 scale-105' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    data-testid={`planner-bar-color-${c.name.toLowerCase()}`}
                  >
                    {plannerBarColor === c.hex && <Check size={16} className="text-white" />}
                  </button>
                ))}
              </div>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('sf-open-onboarding'))}
                className="mt-4 w-full py-2.5 rounded-xl border border-zinc-200 text-sm font-semibold text-zinc-600 hover:bg-zinc-50 transition-colors"
                data-testid="replay-setup-guide-btn"
              >
                Replay setup guide
              </button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium text-zinc-900">Theme Color</p>
                {themeSaving && (
                  <span className="flex items-center gap-1 text-sm text-zinc-500">
                    <Loader2 size={14} className="animate-spin" />
                    Saving...
                  </span>
                )}
              </div>
              <p className="text-sm text-zinc-500 mb-4">Choose a color scheme for the entire application</p>
              <div className="grid grid-cols-7 gap-3">
                {themeColors.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeSelect(theme.id)}
                    disabled={themeSaving}
                    className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      selectedTheme === theme.id 
                        ? 'border-zinc-900 bg-zinc-50' 
                        : 'border-transparent hover:border-zinc-200'
                    } ${themeSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`theme-${theme.id}`}
                  >
                    <div className={`w-10 h-10 rounded-xl ${theme.bgClass} flex items-center justify-center shadow-sm`}>
                      {selectedTheme === theme.id && <Check size={18} className="text-white" />}
                    </div>
                    <span className="text-xs font-medium text-zinc-700">{theme.name}</span>
                  </button>
                ))}
              </div>
              
              {/* Custom Hex Color Picker */}
              <div className="mt-4 p-4 bg-zinc-50 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium text-zinc-700">Custom Color</p>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-lg border-2 border-zinc-200 shadow-inner"
                      style={{ backgroundColor: customThemeColor }}
                    />
                    <input
                      type="color"
                      value={customThemeColor || '#10b981'}
                      onChange={(e) => {
                        setCustomThemeColor(e.target.value);
                      }}
                      className="w-10 h-10 cursor-pointer rounded-lg border-0"
                      data-testid="theme-color-picker"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-500">Hex:</span>
                  <input
                    type="text"
                    value={customThemeColor || '#10b981'}
                    onChange={(e) => {
                      let hex = e.target.value;
                      if (hex && !hex.startsWith('#')) hex = '#' + hex;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                        setCustomThemeColor(hex);
                      }
                    }}
                    placeholder="#10b981"
                    className="w-28 px-2 py-1 text-sm border border-zinc-200 rounded-lg font-mono"
                    data-testid="theme-hex-input"
                  />
                  <button
                    onClick={handleCustomColorApply}
                    disabled={themeSaving}
                    className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                      selectedTheme === 'custom'
                        ? 'bg-zinc-900 text-white'
                        : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'
                    } ${themeSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="apply-custom-theme-btn"
                  >
                    Apply
                  </button>
                </div>
              </div>

              {/* Theme Preview */}
              <div className="mt-4 p-4 bg-white rounded-xl border border-zinc-200">
                <p className="text-sm font-medium text-zinc-700 mb-3">Preview</p>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: 'var(--theme-accent)' }}
                  >
                    AB
                  </div>
                  <div 
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: 'var(--theme-button)' }}
                  >
                    Sample Button
                  </div>
                  <div 
                    className="w-16 h-8 rounded-lg"
                    style={{ backgroundColor: 'var(--theme-sidebar)' }}
                  />
                </div>
              </div>
            </div>

            <SettingRow 
              title="Dark Mode" 
              subtitle="Reduce eye strain in low light"
              action={
                <div className="flex items-center gap-3">
                  {darkMode ? <Moon size={18} className="text-zinc-600" /> : <Sun size={18} className="text-amber-500" />}
                  <Switch checked={darkMode} onCheckedChange={setDarkMode} data-testid="dark-mode-toggle" />
                </div>
              }
            />
            <SettingRow title="Compact View" subtitle="Show more items per page" action={<Switch />} />
            <SettingRow title="Fixed Sidebar" subtitle="Keep sidebar always visible" action={<Switch defaultChecked />} />
          </div>
        );

      case 'navigation':
        return (
          <div className="space-y-6">
            <div className="p-4 bg-zinc-50 rounded-xl space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Navigation size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Default Navigation App</p>
                  <p className="text-xs text-zinc-500">Choose your preferred app for directions</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => saveNavigationSettings('google_maps', navigationMode)}
                  disabled={navSaving}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    navigationApp === 'google_maps' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  } ${navSaving ? 'opacity-50' : ''}`}
                  data-testid="nav-google-maps"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <MapPin size={24} className="text-blue-600" />
                  </div>
                  <span className="font-medium text-zinc-900">Google Maps</span>
                  {navigationApp === 'google_maps' && (
                    <Check size={16} className="text-blue-500" />
                  )}
                </button>

                <button
                  onClick={() => saveNavigationSettings('waze', navigationMode)}
                  disabled={navSaving}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    navigationApp === 'waze' 
                      ? 'border-cyan-500 bg-cyan-50' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  } ${navSaving ? 'opacity-50' : ''}`}
                  data-testid="nav-waze"
                >
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                    <Navigation size={24} className="text-cyan-600" />
                  </div>
                  <span className="font-medium text-zinc-900">Waze</span>
                  {navigationApp === 'waze' && (
                    <Check size={16} className="text-cyan-500" />
                  )}
                </button>
              </div>

              {navigationApp && (
                <button
                  onClick={clearNavigationPreference}
                  disabled={navSaving}
                  className="w-full mt-2 p-2 text-sm text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  Clear preference (ask me each time)
                </button>
              )}
            </div>

            <div className="p-4 bg-zinc-50 rounded-xl space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MapPin size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Location Preference</p>
                  <p className="text-xs text-zinc-500">How to send location to navigation app</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => saveNavigationSettings(navigationApp || '', 'address')}
                  disabled={navSaving}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    navigationMode === 'address' 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  } ${navSaving ? 'opacity-50' : ''}`}
                  data-testid="nav-mode-address"
                >
                  <span className="font-medium text-zinc-900">Address</span>
                  <span className="text-xs text-zinc-500 text-center">Use street address</span>
                  {navigationMode === 'address' && (
                    <Check size={16} className="text-emerald-500" />
                  )}
                </button>

                <button
                  onClick={() => saveNavigationSettings(navigationApp || '', 'coordinates')}
                  disabled={navSaving}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    navigationMode === 'coordinates' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  } ${navSaving ? 'opacity-50' : ''}`}
                  data-testid="nav-mode-coordinates"
                >
                  <span className="font-medium text-zinc-900">Coordinates</span>
                  <span className="text-xs text-zinc-500 text-center">Use GPS location (more precise)</span>
                  {navigationMode === 'coordinates' && (
                    <Check size={16} className="text-purple-500" />
                  )}
                </button>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Tap any address in the Work Planner to open navigation. 
                {navigationApp ? ` It will automatically open in ${navigationApp === 'google_maps' ? 'Google Maps' : 'Waze'}.` : ' You\'ll be asked to choose an app each time.'}
              </p>
            </div>

            {/* Route Optimisation */}
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <MapPin size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Route Optimisation Start Point</p>
                  <p className="text-xs text-zinc-500">
                    Postcode where each day's route should begin (e.g. your home or depot)
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="starting-postcode-input"
                  value={startingPostcode}
                  onChange={(e) => setStartingPostcode(e.target.value.toUpperCase())}
                  placeholder="BN17 5SH"
                  className="h-10 text-sm flex-1"
                  maxLength={10}
                />
                <Button
                  data-testid="save-starting-postcode-btn"
                  onClick={saveStartingPostcode}
                  disabled={savingStartingPostcode}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {savingStartingPostcode ? 'Saving…' : 'Save'}
                </Button>
              </div>
              {startingPostcodeMessage && (
                <p className={`text-xs ${startingLat != null ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {startingPostcodeMessage}
                </p>
              )}
              {startingLat != null && startingLng != null && (
                <p className="text-[11px] text-zinc-500">
                  Coordinates: {startingLat.toFixed(4)}, {startingLng.toFixed(4)}
                </p>
              )}
              <p className="text-xs text-emerald-800">
                Each day's jobs on Work Planner will be ordered nearest-neighbour from this point.
                Use the toggle on Work Planner to flip between <strong>nearest first</strong> and <strong>furthest first</strong>.
              </p>
            </div>
          </div>
        );

      case 'social':
        return (
          <div className="space-y-4">
            {/* Google Reviews - Most Important */}
            <div className="p-4 bg-amber-50 rounded-xl space-y-3 border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Star size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900">Google Reviews Link</p>
                    <p className="text-xs text-zinc-500">Added to invoices & emails to collect reviews</p>
                  </div>
                </div>
              </div>
              <Input
                value={socialLinks.google_reviews_url}
                onChange={(e) => setSocialLinks(prev => ({ ...prev, google_reviews_url: e.target.value }))}
                placeholder="https://g.page/r/YOUR_REVIEW_LINK"
                className="h-9 text-sm"
              />
            </div>

            {/* Website & Social Links */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-medium text-zinc-900">Website & Social Links</p>
                <div className="flex items-center gap-2">
                  {socialSaveMessage && (
                    <span className="text-xs text-emerald-600">{socialSaveMessage}</span>
                  )}
                  <button
                    onClick={saveSocialLinks}
                    disabled={savingSocial}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-xs font-medium rounded-lg"
                  >
                    {savingSocial ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-zinc-500">These links appear in your email signature and invoices</p>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Website</label>
                  <Input
                    value={socialLinks.website_url}
                    onChange={(e) => setSocialLinks(prev => ({ ...prev, website_url: e.target.value }))}
                    placeholder="https://yourwebsite.com"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Facebook</label>
                  <Input
                    value={socialLinks.facebook_url}
                    onChange={(e) => setSocialLinks(prev => ({ ...prev, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/yourpage"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Instagram</label>
                  <Input
                    value={socialLinks.instagram_url}
                    onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram_url: e.target.value }))}
                    placeholder="https://instagram.com/yourhandle"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Twitter / X</label>
                  <Input
                    value={socialLinks.twitter_url}
                    onChange={(e) => setSocialLinks(prev => ({ ...prev, twitter_url: e.target.value }))}
                    placeholder="https://twitter.com/yourhandle"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">LinkedIn</label>
                  <Input
                    value={socialLinks.linkedin_url}
                    onChange={(e) => setSocialLinks(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/company/yourcompany"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">TikTok</label>
                  <Input
                    value={socialLinks.tiktok_url}
                    onChange={(e) => setSocialLinks(prev => ({ ...prev, tiktok_url: e.target.value }))}
                    placeholder="https://tiktok.com/@yourhandle"
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'messaging':
        return (
          <div className="space-y-4">
            {/* Help Text — clickable variable chips that insert into the
                most-recently-focused template field. */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
              <p className="text-sm text-blue-800">
                <strong>Template Variables</strong> — tap a chip to insert it into whichever message field you last clicked. They&apos;re replaced with real values when messages are sent.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  '{customer_name}',
                  '{service}',
                  '{assignee}',
                  '{business_name}',
                  '{date}',
                  '{time}',
                  '{amount}',
                  '{invoice_number}',
                  '{invoice_link}',
                  '{address}',
                ].map((token) => (
                  <button
                    key={token}
                    type="button"
                    data-testid={`template-token-${token.replace(/[{}]/g, '')}`}
                    onClick={() => insertTemplateToken(token)}
                    className="text-xs font-mono px-2 py-1 rounded-md bg-white text-blue-700 border border-blue-300 hover:bg-blue-100 active:scale-95 transition"
                  >
                    {token}
                  </button>
                ))}
              </div>
              <div className="pt-2 mt-2 border-t border-blue-200">
                <p className="text-xs text-blue-800 mb-2">
                  <strong>Squeegee-style chips</strong> — same idea, alternative syntax. Mix and match freely.
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    '[customerName]',
                    '[firstname]',
                    '[lastname]',
                    '[customerAddress]',
                    '[scheduledDate]',
                    '[nextDate]',
                    '[balance]',
                    '[assignedTo]',
                    '[assignedToFirstName]',
                    '[addCardLink]',
                  ].map((token) => (
                    <button
                      key={token}
                      type="button"
                      data-testid={`template-chip-${token.replace(/[[\]]/g, '')}`}
                      onClick={() => insertTemplateToken(token)}
                      className="text-xs font-mono px-2 py-1 rounded-md bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-100 active:scale-95 transition"
                    >
                      {token}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-indigo-700/80 mt-2">
                  <strong>[addCardLink]</strong> drops in a unique, per-customer Stripe link so they can save a card on file in 30 seconds. It&apos;s auto-included in invoice emails — handy for one-off SMS too.
                </p>
              </div>
              {!templateFocusFieldRef.current && (
                <p className="text-[11px] text-blue-700/70">
                  Tip: tap inside any subject or body field below first, then tap a chip.
                </p>
              )}
            </div>

            {/* Email Reminder Template */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Mail size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900">Email Appointment Reminder</p>
                    <p className="text-xs text-zinc-500">Sent to customers the day before their appointment</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Subject Line</label>
                <Input
                  value={messagingTemplates.email_reminder_subject}
                  onChange={(e) => setMessagingTemplates(prev => ({ ...prev, email_reminder_subject: e.target.value }))}
                  onFocus={(e) => handleTemplateFieldFocus('email_reminder_subject', e)}
                  placeholder="Reminder: {service} Tomorrow - {business_name}"
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Email Body</label>
                <textarea
                  value={messagingTemplates.email_reminder_body}
                  onChange={(e) => setMessagingTemplates(prev => ({ ...prev, email_reminder_body: e.target.value }))}
                  onFocus={(e) => handleTemplateFieldFocus('email_reminder_body', e)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm resize-none"
                  placeholder="Hi {customer_name}, this is a reminder..."
                />
              </div>
            </div>

            {/* ClickSend Sender ID — controls who outbound SMS appears from. */}
            <EmailDomainPanel />
            <ClickSendSenderPanel />


            {/* SMS Reminder Template */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <MessageSquare size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">SMS Appointment Reminder</p>
                  <p className="text-xs text-zinc-500">Text message sent the day before appointments</p>
                </div>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">SMS Message (320 char limit — 2 segments)</label>
                <textarea
                  value={messagingTemplates.sms_reminder}
                  onChange={(e) => setMessagingTemplates(prev => ({ ...prev, sms_reminder: e.target.value }))}
                  onFocus={(e) => handleTemplateFieldFocus('sms_reminder', e)}
                  rows={3}
                  maxLength={320}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm resize-none"
                  placeholder="Hi {customer_name}, reminder: your {service} is tomorrow..."
                />
                <p className="text-xs text-zinc-400 mt-1 text-right">
                  {messagingTemplates.sms_reminder.length}/320 characters
                </p>
              </div>
            </div>

            {/* WhatsApp Reminder Template — READ-ONLY (managed in Meta) */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3" data-testid="whatsapp-reminder-template-panel">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <MessageCircle size={20} className="text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-zinc-900">WhatsApp Appointment Reminder</p>
                  <p className="text-xs text-zinc-500">Approved Meta template — body text is fixed by Meta, edit it on Meta Business Manager</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-zinc-200 space-y-2">
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Current template body (read-only)</p>
                <pre className="text-sm text-zinc-700 whitespace-pre-wrap font-sans">{`Hi {{1}}, {{2}} will be with you {{3}} for the {{4}} at {{5}}.

Please can I ask that any rear entrance is unlocked. (If applicable).

Thank you
Perfect Panes`}</pre>
              </div>

              <div className="bg-white rounded-lg p-3 border border-zinc-200">
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">Variable mapping</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2"><span className="font-mono text-xs px-1.5 py-0.5 bg-zinc-100 rounded">{'{{1}}'}</span><span className="text-zinc-700">Customer first name</span><span className="text-zinc-400 text-xs ml-auto">e.g. Daniel</span></div>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs px-1.5 py-0.5 bg-zinc-100 rounded">{'{{2}}'}</span><span className="text-zinc-700">Assigned worker name</span><span className="text-zinc-400 text-xs ml-auto">e.g. Dan</span></div>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded">{'{{3}}'}</span><span className="text-zinc-700">Scheduled date</span><span className="text-zinc-400 text-xs ml-auto">e.g. Friday 16 May</span></div>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs px-1.5 py-0.5 bg-zinc-100 rounded">{'{{4}}'}</span><span className="text-zinc-700">Service name</span><span className="text-zinc-400 text-xs ml-auto">e.g. Window Clean</span></div>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs px-1.5 py-0.5 bg-zinc-100 rounded">{'{{5}}'}</span><span className="text-zinc-700">Address (first line)</span><span className="text-zinc-400 text-xs ml-auto">e.g. 38 Henry Burt Way</span></div>
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 leading-relaxed">
                <strong>Why can&apos;t I edit this here?</strong> WhatsApp Business API requires every template to be pre-approved by Meta. The CRM only fills in the variables — the wording itself lives in your Meta Business Manager.
              </div>

              <a
                href="https://business.facebook.com/wa/manage/message-templates/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="manage-whatsapp-templates-btn"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <ExternalLink size={16} />
                Manage WhatsApp Templates on Meta
              </a>
            </div>

            {/* Invoice Email Template */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <FileText size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Invoice Email Subject</p>
                  <p className="text-xs text-zinc-500">Subject line for invoice emails</p>
                </div>
              </div>
              <Input
                value={messagingTemplates.email_invoice_subject}
                onChange={(e) => setMessagingTemplates(prev => ({ ...prev, email_invoice_subject: e.target.value }))}
                onFocus={(e) => handleTemplateFieldFocus('email_invoice_subject', e)}
                placeholder="Invoice #{invoice_number} from {business_name}"
                className="h-9 text-sm"
              />
            </div>

            {/* SMS Invoice Template */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <CreditCard size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">SMS Invoice Notification</p>
                  <p className="text-xs text-zinc-500">Quick payment link via SMS</p>
                </div>
              </div>
              <textarea
                value={messagingTemplates.sms_invoice}
                onChange={(e) => setMessagingTemplates(prev => ({ ...prev, sms_invoice: e.target.value }))}
                onFocus={(e) => handleTemplateFieldFocus('sms_invoice', e)}
                rows={3}
                maxLength={320}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm resize-none"
                placeholder="Hi {customer_name}, your invoice for £{amount} is ready..."
              />
              <p className="text-xs text-zinc-400 mt-1 text-right">
                {messagingTemplates.sms_invoice.length}/320 characters
              </p>
            </div>

            {/* Email Signature Section */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <FileSignature size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-zinc-900">Email Signature</p>
                  <p className="text-xs text-zinc-500">Customize how your emails sign off</p>
                </div>
              </div>
              
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Signature Text</label>
                <textarea
                  value={invoiceSettings.email_signature || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, email_signature: e.target.value }))}
                  placeholder="e.g., Thanks for your business!&#10;Best regards,&#10;The Perfect Panes Team"
                  className="w-full p-3 border border-zinc-200 rounded-lg text-sm resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                <div>
                  <p className="text-sm font-medium text-zinc-900">Include Logo in Emails</p>
                  <p className="text-xs text-zinc-500">Show your company logo in email footer</p>
                </div>
                <Switch 
                  checked={invoiceSettings.include_logo_in_signature !== false}
                  onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, include_logo_in_signature: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                <div>
                  <p className="text-sm font-medium text-zinc-900">Include Social Links</p>
                  <p className="text-xs text-zinc-500">Show Facebook, Instagram icons in footer</p>
                </div>
                <Switch 
                  checked={invoiceSettings.include_social_in_signature !== false}
                  onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, include_social_in_signature: checked }))}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="flex items-center justify-end gap-3 pt-2">
              {messagingSaveMessage && (
                <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                  <Check size={14} /> {messagingSaveMessage}
                </span>
              )}
              <button
                onClick={saveMessagingTemplates}
                disabled={savingMessaging}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-sm font-medium rounded-lg transition-colors"
                data-testid="save-messaging-templates-btn"
              >
                {savingMessaging ? 'Saving...' : 'Save Templates'}
              </button>
            </div>

            {/* -- Payment Reminders subsection --------------------------------
                User asked (May 2026) for payment-reminder templates inside
                the Messaging settings so SMS + Email reminders are managed
                alongside the other customer-facing templates rather than
                hidden behind a separate sidebar tab. The dedicated
                `payment-reminders` tab is kept for direct deep-linking, but
                this is where users will actually find and edit them.
            */}
            <div className="mt-6 pt-6 border-t border-zinc-200">
              <div className="mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" />
                <h3 className="font-semibold text-zinc-900">
                  Payment Reminders (Owings)
                </h3>
              </div>
              <p className="text-sm text-zinc-600 mb-4">
                These templates are sent when you tap <em>Request Payment</em> on the Owings page.
                Customers with email get the email version; customers with a phone get the SMS (or WhatsApp fallback). Edit either or both.
              </p>
              <PaymentRemindersSection />
            </div>
          </div>
        );

      case 'email-assistant':
        return <EmailAssistantSection />;
      case 'claude-connector':
        return <ClaudeConnectorSection />;
      case 'notifications':
        return (
          <div className="space-y-3">
            {/* Push Notifications Section */}
            <PushNotificationsSection />

            {/* Handover Mode — disable billing & comms on job complete */}
            <div
              className={`p-4 rounded-xl space-y-2 border-2 ${
                handoverMode
                  ? 'bg-amber-50 border-amber-300'
                  : 'bg-zinc-50 border-transparent'
              }`}
              data-testid="handover-mode-section"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-zinc-900">Handover Mode</p>
                    {handoverMode && (
                      <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 uppercase tracking-wide"
                        data-testid="handover-mode-active-badge"
                      >
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">
                    Turn this on if you&apos;ve <strong>sold your rounds</strong> to another company but
                    are still using the Work Planner for route lists during a handover period.
                  </p>
                  <p className="text-xs text-zinc-600 mt-2">
                    When ON, pressing <strong>Done</strong> on a job will:
                  </p>
                  <ul className="text-xs text-zinc-600 mt-1 ml-4 list-disc space-y-0.5">
                    <li>Roll the schedule forward to the next due date</li>
                    <li><strong>NOT</strong> create an invoice</li>
                    <li><strong>NOT</strong> add to the customer&apos;s balance / Owings list</li>
                    <li><strong>NOT</strong> send any SMS, Email or WhatsApp</li>
                    <li><strong>NOT</strong> auto-charge via Stripe or GoCardless</li>
                  </ul>
                  <p className="text-xs text-amber-700 mt-2 font-medium">
                    ⚠ This affects every job completed under this account. Only turn on for
                    accounts whose rounds belong to another company.
                  </p>
                </div>
                <Switch
                  checked={handoverMode}
                  onCheckedChange={(checked) => {
                    setHandoverMode(checked);
                    saveHandoverMode(checked);
                  }}
                  data-testid="handover-mode-toggle"
                />
              </div>
            </div>

            {/* Auto-send invoice on Done — for customers without saved payment method */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3" data-testid="auto-send-invoice-section">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-zinc-900">Auto-send invoice on Done</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    When you mark a job as done for a customer who <strong>doesn't</strong> have a
                    saved Stripe card or GoCardless mandate, the invoice will be sent automatically
                    without prompting — keeping you moving on the route.
                    Customers with saved payment methods will continue to be auto-charged as normal.
                  </p>
                </div>
                <Switch
                  checked={autoSendInvoice}
                  onCheckedChange={(checked) => {
                    setAutoSendInvoice(checked);
                    saveAutoSendInvoice(checked, autoSendMethod);
                  }}
                  data-testid="auto-send-invoice-toggle"
                />
              </div>

              {autoSendInvoice && (
                <div data-testid="auto-send-method-picker">
                  <label className="text-xs text-zinc-500 mb-2 block">Send via</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'whatsapp_sms', label: 'WhatsApp / SMS', hint: 'WA first, SMS fallback' },
                      { value: 'email', label: 'Email', hint: 'Email only' },
                      { value: 'both', label: 'Both', hint: 'WhatsApp + Email' },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => {
                          setAutoSendMethod(opt.value);
                          saveAutoSendInvoice(autoSendInvoice, opt.value);
                        }}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          autoSendMethod === opt.value
                            ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-300'
                            : 'bg-white border-zinc-200 hover:border-zinc-300'
                        }`}
                        data-testid={`auto-send-method-${opt.value}`}
                      >
                        <p className="text-sm font-medium text-zinc-900">{opt.label}</p>
                        <p className="text-[11px] text-zinc-500 mt-0.5">{opt.hint}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <SettingRow title="Default Method" subtitle="Email" actionText="Change" />
            <div className="p-4 bg-zinc-50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium text-zinc-900">SMS Credits</p>
                <button className="text-sm text-emerald-600 font-medium">Buy More</button>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1 h-2 bg-zinc-200 rounded-full overflow-hidden">
                  <div className="h-full w-3/4 bg-emerald-500 rounded-full" />
                </div>
                <span className="text-sm font-medium text-zinc-700">247 credits</span>
              </div>
            </div>
            {/* Email sender — surfaces the actual server-configured `From:`
                address (was hardcoded to a stale value before). Editing it
                requires verifying a new domain in Resend, so we link to
                support rather than offer a noop "Configure" CTA. */}
            <div
              className="p-4 bg-zinc-50 rounded-xl flex items-start justify-between gap-3"
              data-testid="email-sender-row"
            >
              <div className="min-w-0">
                <p className="font-medium text-zinc-900">Email Address (From)</p>
                <p className="text-sm text-zinc-700 mt-0.5 break-all" data-testid="email-sender-value">
                  {emailSenderInfo?.sender_email || 'Loading…'}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  Customers see this as the sender. Changing it requires verifying a new
                  domain in Resend — contact support.
                </p>
              </div>
              <button
                onClick={() => {
                  if (emailSenderInfo?.change_instructions) {
                    toast.info(emailSenderInfo.change_instructions, { duration: 10000 });
                  }
                }}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex-shrink-0"
                data-testid="email-sender-help-btn"
              >
                How to change?
              </button>
            </div>

            {/* CC / BCC every outgoing invoice email */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div>
                <p className="font-medium text-zinc-900">Send a copy of every invoice email</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Useful for bookkeepers, second inboxes, or shared visibility (e.g. bookings@). Comma-separate for multiple.
                </p>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">CC (visible to customer)</label>
                <input
                  type="text"
                  value={invoiceSettings.cc_invoice_emails || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, cc_invoice_emails: e.target.value }))}
                  onBlur={saveInvoiceSettings}
                  placeholder="bookings@perfect-panes.com"
                  className="w-full p-3 border border-zinc-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="cc-invoice-emails-input"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">BCC (hidden from customer)</label>
                <input
                  type="text"
                  value={invoiceSettings.invoice_email_bcc || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_email_bcc: e.target.value }))}
                  onBlur={saveInvoiceSettings}
                  placeholder="archive@perfect-panes.com"
                  className="w-full p-3 border border-zinc-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="bcc-invoice-emails-input"
                />
              </div>
              {invoiceSaveMessage && (
                <p className="text-xs text-emerald-600">{invoiceSaveMessage}</p>
              )}
            </div>
            
            {/* Forward incoming SMS replies to a mobile (so the user sees
                customer replies on their phone in addition to the in-app
                Messages inbox). Has an explicit Save button because the
                onBlur autosave doesn't reliably fire on PWAs when the user
                dismisses the keyboard without tabbing — was leaving the
                input empty in production. */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-2" data-testid="sms-forward-block">
              <p className="font-medium text-zinc-900">Forward SMS replies to my phone</p>
              <p className="text-xs text-zinc-500 -mt-1">
                When a customer replies to an SMS we sent, the reply also gets texted to this number so you see it on your phone.
              </p>
              <div className="flex gap-2 items-stretch">
                <input
                  type="tel"
                  value={invoiceSettings.sms_forward_to || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, sms_forward_to: e.target.value }))}
                  onBlur={saveInvoiceSettings}
                  placeholder="07951313755"
                  className="flex-1 min-w-0 p-3 border border-zinc-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="sms-forward-input"
                />
                <button
                  type="button"
                  onClick={async () => {
                    await saveInvoiceSettings();
                    toast.success('SMS forwarding number saved');
                  }}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg whitespace-nowrap transition-colors"
                  data-testid="sms-forward-save-btn"
                >
                  Save
                </button>
              </div>
              <p className="text-[11px] text-zinc-400">UK mobile. Leave empty to disable forwarding.</p>
              {invoiceSaveMessage && (
                <p className="text-xs text-emerald-600">{invoiceSaveMessage}</p>
              )}
            </div>
            
            {/* Email Signature Section */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <p className="font-medium text-zinc-900">Email Signature</p>
              <p className="text-xs text-zinc-500 -mt-2">Customize how your emails sign off</p>
              
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Signature Text</label>
                  <textarea
                    value={invoiceSettings.email_signature || ''}
                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, email_signature: e.target.value }))}
                    placeholder="e.g., Thanks for your business!&#10;Best regards,&#10;The Perfect Panes Team"
                    className="w-full p-3 border border-zinc-200 rounded-lg text-sm resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    rows={3}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Include Logo in Signature</p>
                    <p className="text-xs text-zinc-500">Show your company logo at the bottom of emails</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.include_logo_in_signature !== false}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, include_logo_in_signature: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Include Social Links</p>
                    <p className="text-xs text-zinc-500">Show Facebook, Instagram, etc. in footer</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.include_social_in_signature !== false}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, include_social_in_signature: checked }))}
                  />
                </div>
              </div>
            </div>
            
            <div className="pt-4 mt-4 border-t border-zinc-200">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-zinc-500">MESSAGE TEMPLATES</p>
                <button 
                  onClick={() => setShowTemplateEditor(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="open-template-editor-btn"
                >
                  <Edit3 size={14} />
                  Edit Templates
                </button>
              </div>
              
              {/* Email Templates */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Mail size={12} />
                  Email Templates
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText size={14} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">Invoice Notification</p>
                        <p className="text-xs text-zinc-500">Sent when invoice is created</p>
                      </div>
                    </div>
                    <button onClick={() => setShowTemplateEditor(true)} className="text-xs text-emerald-600 font-medium hover:underline">Edit</button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Bell size={14} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">Payment Reminder</p>
                        <p className="text-xs text-zinc-500">Sent before due date</p>
                      </div>
                    </div>
                    <button onClick={() => setShowTemplateEditor(true)} className="text-xs text-emerald-600 font-medium hover:underline">Edit</button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Check size={14} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">Job Completion</p>
                        <p className="text-xs text-zinc-500">Sent when job is done</p>
                      </div>
                    </div>
                    <button onClick={() => setShowTemplateEditor(true)} className="text-xs text-emerald-600 font-medium hover:underline">Edit</button>
                  </div>
                </div>
              </div>
              
              {/* SMS Templates */}
              <div>
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <MessageSquare size={12} />
                  SMS Templates
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Bell size={14} className="text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">Job Reminder</p>
                        <p className="text-xs text-zinc-500">Sent day before appointment</p>
                      </div>
                    </div>
                    <button onClick={() => setShowTemplateEditor(true)} className="text-xs text-emerald-600 font-medium hover:underline">Edit</button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
                        <MessageSquare size={14} className="text-cyan-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">On My Way</p>
                        <p className="text-xs text-zinc-500">Sent when worker is en route</p>
                      </div>
                    </div>
                    <button onClick={() => setShowTemplateEditor(true)} className="text-xs text-emerald-600 font-medium hover:underline">Edit</button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <CreditCard size={14} className="text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">Payment Request</p>
                        <p className="text-xs text-zinc-500">Quick payment link SMS</p>
                      </div>
                    </div>
                    <button onClick={() => setShowTemplateEditor(true)} className="text-xs text-emerald-600 font-medium hover:underline">Edit</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'jobs':
        return (
          <div className="space-y-3">
            <DefaultServiceNameSetting />
            <SettingRow title="Default Duration" subtitle="45 minutes" actionText="Change" />
            <SettingRow title="Auto-schedule" subtitle="Automatically schedule recurring jobs" action={<Switch defaultChecked />} />
            <SettingRow title="Require Photos" subtitle="Before/after photos required" action={<Switch />} />
            <SettingRow title="GPS Tracking" subtitle="Track staff during jobs" action={<Switch defaultChecked />} />
          </div>
        );

      case 'invoicing':
        return (
          <div className="space-y-4">
            {/* Business Details for Invoices */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-medium text-zinc-900">Business Details</p>
                <div className="flex items-center gap-2">
                  {bankingSaveMessage && (
                    <span className="text-xs text-emerald-600">{bankingSaveMessage}</span>
                  )}
                  <button
                    onClick={saveBankingSettings}
                    disabled={savingBanking}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-xs font-medium rounded-lg"
                  >
                    {savingBanking ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Business Address</label>
                  <Input
                    value={bankingSettings.business_address}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, business_address: e.target.value }))}
                    placeholder="123 High Street, Town"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Business Email</label>
                  <Input
                    value={bankingSettings.business_email}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, business_email: e.target.value }))}
                    placeholder="accounts@company.com"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Business Phone</label>
                  <Input
                    value={bankingSettings.business_phone}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, business_phone: e.target.value }))}
                    placeholder="01onal 123456"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">VAT Number (optional)</label>
                  <Input
                    value={bankingSettings.vat_number}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, vat_number: e.target.value }))}
                    placeholder="GB123456789"
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Bank Details for BACS */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-4">
              <p className="font-medium text-zinc-900">Bank Details (for BACS payments)</p>
              <p className="text-sm text-zinc-500">These details will appear on your invoices for bank transfers</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Account Name</label>
                  <Input
                    value={bankingSettings.account_name}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, account_name: e.target.value }))}
                    placeholder="Your Company Ltd"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Bank Name</label>
                  <Input
                    value={bankingSettings.bank_name}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="Barclays, Lloyds, etc."
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Sort Code</label>
                  <Input
                    value={bankingSettings.sort_code}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, sort_code: e.target.value }))}
                    placeholder="00-00-00"
                    className="h-9 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Account Number</label>
                  <Input
                    value={bankingSettings.account_number}
                    onChange={(e) => setBankingSettings(prev => ({ ...prev, account_number: e.target.value }))}
                    placeholder="12345678"
                    className="h-9 text-sm font-mono"
                  />
                </div>
              </div>
            </div>
            
            {/* VAT Settings */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">VAT / Tax</p>
                  <p className="text-sm text-zinc-500">Add VAT/tax to invoices and owing amounts</p>
                </div>
                <div className="flex items-center gap-2">
                  {vatSaveMessage && (
                    <span className="text-xs text-emerald-600">{vatSaveMessage}</span>
                  )}
                  <Switch 
                    checked={vatEnabled} 
                    onCheckedChange={handleVatToggle}
                    disabled={savingVat}
                    data-testid="vat-toggle"
                  />
                </div>
              </div>
              
              {vatEnabled && (
                <div className="space-y-3 pt-2 border-t border-zinc-200">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Percent size={16} className="text-zinc-400" />
                      <span className="text-sm text-zinc-600">Rate:</span>
                    </div>
                    <Input
                      type="number"
                      value={vatPercentage}
                      onChange={(e) => handleVatPercentageChange(e.target.value)}
                      onBlur={handleVatPercentageBlur}
                      className="w-20 h-8 text-sm"
                      min="0"
                      max="100"
                      step="0.1"
                      data-testid="vat-percentage-input"
                    />
                    <span className="text-sm text-zinc-500">%</span>
                    <span className="text-xs text-zinc-400 ml-2">
                      (e.g., £16.67 + {vatPercentage}% = £{(16.67 * (1 + vatPercentage/100)).toFixed(2)})
                    </span>
                  </div>
                  
                  {/* Manual Entry Setting */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-200">
                    <div>
                      <p className="text-sm font-medium text-zinc-700">Manual Entry: Prices include VAT</p>
                      <p className="text-xs text-zinc-500">When adding jobs manually, prices you enter already include VAT</p>
                      <p className="text-xs text-zinc-400 mt-1">
                        {vatPricesIncludeVat 
                          ? `Enter £24 → saves as £${(24 / (1 + vatPercentage/100)).toFixed(2)} net + £${(24 - 24 / (1 + vatPercentage/100)).toFixed(2)} VAT`
                          : `Enter £20 → saves as £20 net + £${(20 * vatPercentage/100).toFixed(2)} VAT = £${(20 * (1 + vatPercentage/100)).toFixed(2)} total`
                        }
                      </p>
                    </div>
                    <Switch 
                      checked={vatPricesIncludeVat} 
                      onCheckedChange={handleVatPricesIncludeVatChange}
                      data-testid="vat-prices-include-vat-toggle"
                    />
                  </div>
                  
                  {/* Bulk Import Setting */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-200">
                    <div>
                      <p className="text-sm font-medium text-zinc-700">Bulk Import: Prices include VAT</p>
                      <p className="text-xs text-zinc-500">When importing from PDF/email, prices already include VAT</p>
                      <p className="text-xs text-zinc-400 mt-1">
                        {vatBulkImportIncludesVat 
                          ? `PDF shows £27 → saves as £${(27 / (1 + vatPercentage/100)).toFixed(2)} net + £${(27 - 27 / (1 + vatPercentage/100)).toFixed(2)} VAT`
                          : `PDF shows £22.50 → saves as £22.50 net + £${(22.50 * vatPercentage/100).toFixed(2)} VAT = £${(22.50 * (1 + vatPercentage/100)).toFixed(2)} total`
                        }
                      </p>
                    </div>
                    <Switch 
                      checked={vatBulkImportIncludesVat} 
                      onCheckedChange={handleVatBulkImportIncludesVatChange}
                      data-testid="vat-bulk-import-includes-vat-toggle"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Default Notes */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Default Invoice Notes</p>
                  <p className="text-xs text-zinc-500">Appears at the bottom of every invoice</p>
                </div>
                <div className="flex items-center gap-2">
                  {invoiceSaveMessage && (
                    <span className="text-xs text-emerald-600">{invoiceSaveMessage}</span>
                  )}
                  <button
                    onClick={saveInvoiceSettings}
                    disabled={savingInvoice}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-xs font-medium rounded-lg"
                  >
                    {savingInvoice ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <textarea
                value={invoiceSettings.invoice_default_notes}
                onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_default_notes: e.target.value }))}
                placeholder="Thank you for your business! Please pay within 14 days.&#10;&#10;Bank: [Bank Name]&#10;Sort Code: XX-XX-XX&#10;Account: XXXXXXXX"
                className="w-full h-24 px-3 py-2 text-sm border border-zinc-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Invoice Numbering */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Invoice Numbering</p>
                  <p className="text-xs text-zinc-500">Customise how invoice numbers are generated</p>
                </div>
                <div className="flex items-center gap-2">
                  {invoiceSaveMessage && (
                    <span className="text-xs text-emerald-600">{invoiceSaveMessage}</span>
                  )}
                  <button
                    onClick={saveInvoiceSettings}
                    disabled={savingInvoice}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-xs font-medium rounded-lg"
                    data-testid="save-invoice-numbering-btn"
                  >
                    {savingInvoice ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Prefix</label>
                  <Input
                    value={invoiceSettings.invoice_prefix}
                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_prefix: e.target.value.toUpperCase() }))}
                    placeholder="INV"
                    maxLength={10}
                    className="h-9 text-sm font-mono"
                    data-testid="invoice-prefix-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Next Number</label>
                  <Input
                    type="number"
                    value={invoiceSettings.invoice_next_number}
                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_next_number: Math.max(1, parseInt(e.target.value) || 1) }))}
                    min="1"
                    className="h-9 text-sm font-mono"
                    data-testid="invoice-next-number-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Format</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-2 bg-white rounded-lg border border-zinc-200 cursor-pointer hover:border-emerald-300">
                    <input
                      type="radio"
                      name="invoice_format"
                      value="prefix-year-seq"
                      checked={invoiceSettings.invoice_number_format === 'prefix-year-seq'}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_number_format: e.target.value }))}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-zinc-700">{invoiceSettings.invoice_prefix}-{new Date().getFullYear()}-{String(invoiceSettings.invoice_next_number).padStart(4, '0')}</p>
                      <p className="text-xs text-zinc-400">Prefix + Year + Sequential (resets each year)</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-2 bg-white rounded-lg border border-zinc-200 cursor-pointer hover:border-emerald-300">
                    <input
                      type="radio"
                      name="invoice_format"
                      value="prefix-seq"
                      checked={invoiceSettings.invoice_number_format === 'prefix-seq'}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_number_format: e.target.value }))}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-zinc-700">{invoiceSettings.invoice_prefix}-{String(invoiceSettings.invoice_next_number).padStart(4, '0')}</p>
                      <p className="text-xs text-zinc-400">Prefix + Sequential (never resets)</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-2 bg-white rounded-lg border border-zinc-200 cursor-pointer hover:border-emerald-300">
                    <input
                      type="radio"
                      name="invoice_format"
                      value="seq-only"
                      checked={invoiceSettings.invoice_number_format === 'seq-only'}
                      onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_number_format: e.target.value }))}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-zinc-700">{String(invoiceSettings.invoice_next_number).padStart(4, '0')}</p>
                      <p className="text-xs text-zinc-400">Sequential number only</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Invoice Automation */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium text-zinc-900">Invoice Automation</p>
                <div className="flex items-center gap-2">
                  {invoiceSaveMessage && (
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                      <Check className="w-3 h-3" /> {invoiceSaveMessage}
                    </span>
                  )}
                  <button
                    onClick={saveInvoiceSettings}
                    disabled={savingInvoice}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white text-xs font-medium rounded-lg"
                    data-testid="save-invoice-automation-btn"
                  >
                    {savingInvoice ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Auto-create invoices</p>
                    <p className="text-xs text-zinc-500">Create invoice when job is completed</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.auto_create_invoices}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, auto_create_invoices: checked }))}
                    data-testid="auto-create-invoices-toggle"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Auto-send invoices</p>
                    <p className="text-xs text-zinc-500">Email invoice to customer automatically (disabled for Stripe/GoCardless customers)</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.auto_send_invoices}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, auto_send_invoices: checked }))}
                    data-testid="auto-send-invoices-toggle"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Show tax on invoices</p>
                    <p className="text-xs text-zinc-500">Display VAT breakdown on invoices</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.show_tax_on_invoices}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, show_tax_on_invoices: checked }))}
                    data-testid="show-tax-toggle"
                  />
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <p className="font-medium text-zinc-900">Payment Terms</p>
              <p className="text-xs text-zinc-500 -mt-2">How long customers have to pay</p>
              
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-zinc-100 cursor-pointer hover:border-emerald-300">
                  <input
                    type="radio"
                    name="payment_terms"
                    checked={invoiceSettings.invoice_payment_terms === 'on_receipt'}
                    onChange={() => setInvoiceSettings(prev => ({ ...prev, invoice_payment_terms: 'on_receipt' }))}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Payment on receipt</p>
                    <p className="text-xs text-zinc-500">Due immediately when invoice is received</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-zinc-100 cursor-pointer hover:border-emerald-300">
                  <input
                    type="radio"
                    name="payment_terms"
                    checked={invoiceSettings.invoice_payment_terms === 'days'}
                    onChange={() => setInvoiceSettings(prev => ({ ...prev, invoice_payment_terms: 'days' }))}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-zinc-900">Payment within X days</p>
                    <p className="text-xs text-zinc-500">Due after a set number of days</p>
                  </div>
                  {invoiceSettings.invoice_payment_terms === 'days' && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={invoiceSettings.invoice_payment_days}
                        onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoice_payment_days: parseInt(e.target.value) || 14 }))}
                        className="w-16 h-8 text-sm text-center"
                        min="1"
                        max="90"
                      />
                      <span className="text-sm text-zinc-500">days</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Invoice Payment Options */}
            <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
              <p className="font-medium text-zinc-900">Payment Options on Invoice</p>
              <p className="text-xs text-zinc-500 -mt-2">Choose which payment methods to show on customer invoices</p>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Pay by Card (Stripe)</p>
                    <p className="text-xs text-zinc-500">Allow customers to pay by card</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.invoice_show_card_payment}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, invoice_show_card_payment: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Pay by Direct Debit (GoCardless)</p>
                    <p className="text-xs text-zinc-500">Allow customers to set up Direct Debit</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.invoice_show_dd_payment}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, invoice_show_dd_payment: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Bank Transfer (BACS)</p>
                    <p className="text-xs text-zinc-500">Show your bank details for transfers</p>
                  </div>
                  <Switch 
                    checked={invoiceSettings.invoice_show_bacs}
                    onCheckedChange={(checked) => setInvoiceSettings(prev => ({ ...prev, invoice_show_bacs: checked }))}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'payment-reminders':
        return <PaymentRemindersSection />;

      case 'users':
        return (
          <div className="space-y-3">
            {loadingTeamUsers ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="animate-spin text-emerald-600" size={24} />
              </div>
            ) : teamUsers.length === 0 ? (
              <div className="text-center p-8 text-zinc-500">
                <Users size={32} className="mx-auto mb-2 text-zinc-300" />
                <p>No team members yet</p>
                <p className="text-sm">Add users to your team below</p>
              </div>
            ) : (
              teamUsers.map(user => (
                <div key={user.id} className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-zinc-900">{user.name || user.email}</p>
                    <p className="text-sm text-zinc-500">{user.role} • {user.is_active !== false ? 'Active' : 'Inactive'}</p>
                  </div>
                  <button className="text-sm text-emerald-600 font-medium">Manage</button>
                </div>
              ))
            )}
            <button 
              onClick={() => setShowAddUserModal(true)}
              className="w-full p-4 border-2 border-dashed border-zinc-300 rounded-xl text-zinc-500 hover:border-emerald-300 hover:text-emerald-500 transition-colors font-medium" 
              data-testid="add-user-btn"
            >
              + Add New User
            </button>
          </div>
        );

      case 'worker-permissions':
        return (
          <div className="space-y-4">
            <p className="text-sm text-zinc-600 mb-4">
              Control what workers can see and do in the app. These settings apply to all users with the &quot;Worker&quot; or &quot;Technician&quot; role.
            </p>
            
            {/* Saved Success Message */}
            {workerPermissionsSaved && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
                <CheckCircle size={20} className="text-emerald-600" />
                <span className="text-emerald-700 font-medium">Settings saved! Workers will see changes immediately.</span>
              </div>
            )}
            
            {/* View Permissions */}
            <div className="bg-zinc-50 rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-zinc-900 text-sm">What Workers Can See</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Job Prices</p>
                  <p className="text-sm text-zinc-500">Show price on job cards</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_see_prices}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_see_prices: checked}))}
                  data-testid="worker-perm-prices"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Customer Balance</p>
                  <p className="text-sm text-zinc-500">Show outstanding balance</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_see_customer_balance}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_see_customer_balance: checked}))}
                  data-testid="worker-perm-balance"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Customer Phone</p>
                  <p className="text-sm text-zinc-500">Show phone number</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_see_customer_phone}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_see_customer_phone: checked}))}
                  data-testid="worker-perm-phone"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Customer Address</p>
                  <p className="text-sm text-zinc-500">Show full address</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_see_customer_address}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_see_customer_address: checked}))}
                  data-testid="worker-perm-address"
                />
              </div>
            </div>
            
            {/* Action Permissions */}
            <div className="bg-zinc-50 rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-zinc-900 text-sm">What Workers Can Do</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Send Reminders</p>
                  <p className="text-sm text-zinc-500">Allow sending SMS/Email reminders</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_send_reminders}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_send_reminders: checked}))}
                  data-testid="worker-perm-reminders"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Create Jobs</p>
                  <p className="text-sm text-zinc-500">Allow creating new jobs</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_create_jobs}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_create_jobs: checked}))}
                  data-testid="worker-perm-create-jobs"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Mark Jobs Done</p>
                  <p className="text-sm text-zinc-500">Allow marking jobs as complete</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_mark_jobs_done}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_mark_jobs_done: checked}))}
                  data-testid="worker-perm-mark-done"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-zinc-900">Skip Jobs</p>
                  <p className="text-sm text-zinc-500">Allow skipping jobs</p>
                </div>
                <Switch 
                  checked={workerPermissions.can_skip_jobs}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_skip_jobs: checked}))}
                  data-testid="worker-perm-skip"
                />
              </div>
              
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-zinc-200">
                <div className="flex-1 pr-3">
                  <p className="font-medium text-zinc-900">Take Payments</p>
                  <p className="text-sm text-zinc-500">
                    Workers can mark jobs as <strong>Done &amp; Paid</strong> (cash, card-on-file, or bank transfer) directly from the field. Leave off if you collect payments yourself.
                  </p>
                </div>
                <Switch 
                  checked={workerPermissions.can_take_payments || false}
                  onCheckedChange={(checked) => setWorkerPermissions(prev => ({...prev, can_take_payments: checked}))}
                  data-testid="worker-perm-take-payments"
                />
              </div>
            </div>
            
            <button 
              onClick={saveWorkerPermissions}
              disabled={savingWorkerPermissions}
              className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-worker-permissions"
            >
              {savingWorkerPermissions ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : workerPermissionsSaved ? (
                <>
                  <CheckCircle size={18} />
                  Saved!
                </>
              ) : (
                'Save Worker Permissions'
              )}
            </button>
          </div>
        );

      case 'integrations':
        return (
          <div className="space-y-3">
            {/* Email Forwarding Section */}
            <EmailForwardingSection />
            
            <StripeConnectPanel
              connected={integrations.stripe?.status === 'connected'}
              accountName={integrations.stripe?.account_name}
              environment={integrations.stripe?.environment}
              onConnected={loadIntegrations}
            />
            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white font-bold">X</div>
                <div>
                  <p className="font-medium text-zinc-900">Xero</p>
                  <p className="text-sm text-emerald-600">Connected</p>
                </div>
              </div>
              <button className="text-sm text-zinc-600 font-medium">Configure</button>
            </div>
            <div className={`p-4 rounded-xl ${integrations.gocardless?.status === 'connected' ? 'bg-emerald-50 border border-emerald-200' : 'bg-zinc-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-500 rounded-xl flex items-center justify-center text-white font-bold">G</div>
                  <div>
                    <p className="font-medium text-zinc-900">GoCardless</p>
                    <p className={`text-sm ${integrations.gocardless?.status === 'connected' ? 'text-emerald-600' : 'text-zinc-500'}`}>
                      {integrations.gocardless?.status === 'connected' ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>
                <button className={`text-sm font-medium ${integrations.gocardless?.status === 'connected' ? 'text-zinc-600' : 'text-emerald-600'}`}>
                  {integrations.gocardless?.status === 'connected' ? 'Configure' : 'Connect'}
                </button>
              </div>
              
              {/* Webhook Configuration - shown when connected */}
              {integrations.gocardless?.status === 'connected' && (
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <p className="text-xs font-medium text-zinc-700 mb-2">Webhook URL (configure in GoCardless dashboard)</p>
                  <div className="flex gap-2">
                    <input 
                      type="text"
                      value={webhookUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs font-mono text-zinc-600 truncate"
                      data-testid="gocardless-webhook-url"
                    />
                    <button
                      onClick={copyWebhookUrl}
                      className="px-3 py-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-lg text-xs font-medium text-zinc-700 transition-colors flex items-center gap-1"
                      data-testid="copy-webhook-url-btn"
                    >
                      {webhookCopied ? <Check size={14} className="text-emerald-600" /> : <Code size={14} />}
                      {webhookCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Add this URL in your GoCardless dashboard under Developers → Webhooks
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold">Q</div>
                <div>
                  <p className="font-medium text-zinc-900">QuickBooks</p>
                  <p className="text-sm text-zinc-500">Not connected</p>
                </div>
              </div>
              <button className="text-sm text-emerald-600 font-medium">Connect</button>
            </div>
          </div>
        );

      case 'developer':
        return (
          <div className="space-y-3">
            <div className="p-4 bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Code size={20} className="text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold">Public API</h3>
                  <p className="text-sm text-zinc-400">Build integrations with our RESTful API</p>
                </div>
              </div>
              <p className="text-xs text-zinc-400 mb-3">
                Access customers, jobs, and invoices programmatically. Perfect for connecting 
                Facebook Lead Ads, bulk importing from other systems, or building custom integrations.
              </p>
              <button 
                onClick={() => onNavigate && onNavigate('api-docs')}
                className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium text-sm transition-colors"
                data-testid="open-api-docs-btn"
              >
                View API Documentation →
              </button>
            </div>
            
            {/* API Keys Management */}
            <div className="bg-white rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-zinc-900">API Keys</h3>
                  <p className="text-sm text-zinc-500">Manage your API keys for external integrations</p>
                </div>
                <button
                  onClick={() => setShowCreateKeyModal(true)}
                  className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                  data-testid="create-api-key-btn"
                >
                  <Plus size={16} />
                  Create Key
                </button>
              </div>
              
              {/* Newly Created Key Warning */}
              {newlyCreatedKey && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">Copy your API key now!</p>
                      <p className="text-xs text-amber-700 mb-2">This key won't be shown again for security reasons.</p>
                      <div className="flex items-center gap-2 bg-white rounded border border-amber-300 p-2">
                        <code className="text-xs text-zinc-800 flex-1 break-all font-mono">{newlyCreatedKey}</code>
                        <button
                          onClick={() => copyApiKey(newlyCreatedKey)}
                          className="p-1.5 hover:bg-amber-100 rounded transition-colors"
                        >
                          <Copy size={14} className="text-amber-600" />
                        </button>
                      </div>
                      <button
                        onClick={() => setNewlyCreatedKey(null)}
                        className="mt-2 text-xs text-amber-600 hover:text-amber-800 font-medium"
                      >
                        I've copied the key, dismiss this
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Existing Keys List */}
              {loadingApiKeys ? (
                <div className="py-8 text-center">
                  <Loader2 size={24} className="animate-spin text-zinc-400 mx-auto" />
                </div>
              ) : !apiKeys || apiKeys.length === 0 ? (
                <div className="py-6 text-center text-zinc-500">
                  <Key size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No API keys yet</p>
                  <p className="text-xs text-zinc-400">Create your first key to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {apiKeys.filter(key => key.is_active !== false).map((key) => (
                    <div key={key.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-zinc-900 text-sm">{key.name}</span>
                          <code className="text-xs bg-zinc-200 px-1.5 py-0.5 rounded text-zinc-600 font-mono">
                            {key.key_prefix}...
                          </code>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-zinc-500">
                            Created: {new Date(key.created_at).toLocaleDateString()}
                          </span>
                          {key.last_used && (
                            <span className="text-xs text-zinc-400">
                              • Last used: {new Date(key.last_used).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 mt-1">
                          {key.scopes?.map((scope) => (
                            <span key={scope} className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {scope}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeApiKey(key.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Revoke key"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Create Key Modal */}
            {showCreateKeyModal && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-zinc-900">Create API Key</h3>
                    <button 
                      onClick={() => {
                        setShowCreateKeyModal(false);
                        setKeyError('');
                        setNewKeyName('');
                      }}
                      className="p-1 hover:bg-zinc-100 rounded-lg"
                    >
                      <X size={20} className="text-zinc-500" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">Key Name</label>
                      <input
                        type="text"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder="e.g., Facebook Lead Ads"
                        className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">Permissions</label>
                      <div className="space-y-3">
                        {['read', 'write', 'delete', 'admin'].map((scope) => (
                          <label key={scope} className="flex items-center gap-3 cursor-pointer p-2 -m-2 rounded-lg active:bg-zinc-100">
                            <input
                              type="checkbox"
                              checked={newKeyScopes.includes(scope)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewKeyScopes([...newKeyScopes, scope]);
                                } else {
                                  setNewKeyScopes(newKeyScopes.filter(s => s !== scope));
                                }
                              }}
                              className="w-5 h-5 rounded border-zinc-300 text-emerald-500 focus:ring-emerald-500 flex-shrink-0"
                            />
                            <div className="flex flex-col">
                              <span className="text-sm text-zinc-700 capitalize font-medium">{scope}</span>
                              <span className="text-xs text-zinc-400">
                                {scope === 'read' && 'View customers, jobs, invoices'}
                                {scope === 'write' && 'Create/update data'}
                                {scope === 'delete' && 'Delete data'}
                                {scope === 'admin' && 'Full access'}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {keyError && (
                      <p className="text-sm text-red-600">{keyError}</p>
                    )}
                    
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => {
                          setShowCreateKeyModal(false);
                          setKeyError('');
                          setNewKeyName('');
                        }}
                        className="flex-1 py-2 border border-zinc-300 text-zinc-700 rounded-lg font-medium text-sm hover:bg-zinc-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateApiKey}
                        disabled={creatingKey}
                        className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {creatingKey ? <Loader2 size={16} className="animate-spin" /> : <Key size={16} />}
                        Create Key
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-xl">
              <div>
                <p className="font-medium text-zinc-900">Rate Limits</p>
                <p className="text-sm text-zinc-500">60 requests/minute, 10,000/day</p>
              </div>
              <span className="text-sm text-zinc-400 font-medium">Info only</span>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <Globe size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-blue-900 text-sm">How to use API Keys</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Include your API key in the X-API-Key header when making requests.
                    Example: <code className="bg-blue-100 px-1 rounded">X-API-Key: pp_live_xxxxx</code>
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-3">
            <SettingRow title="Change Password" subtitle="Last changed 30 days ago" actionText="Change" />
            <SettingRow title="Two-Factor Authentication" subtitle="Add extra security" action={<Switch />} />
            <SettingRow title="Active Sessions" subtitle="2 devices logged in" actionText="Manage" />
            <SettingRow title="Login History" subtitle="View recent activity" actionText="View" />
          </div>
        );

      case 'data-export':
        return <DataExportSection />;

      case 'mobile':
        return (
          <div className="space-y-3">
            <SettingRow title="Offline Mode" subtitle="Work without internet" action={<Switch defaultChecked />} />
            <SettingRow title="Auto-sync" subtitle="Sync when connected" action={<Switch defaultChecked />} />
            <SettingRow title="Photo Quality" subtitle="High (uses more data)" actionText="Change" />
            <SettingRow title="Push Notifications" subtitle="Receive alerts" action={<Switch defaultChecked />} />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-zinc-100" data-testid="settings-page">
      {/* Header */}
      <div className="bg-zinc-900 text-white px-4 py-4 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
        <button 
          onClick={onMenuClick}
          className="p-2 -ml-2 hover:bg-white/10 rounded-xl transition-colors"
        >
          <Menu size={22} strokeWidth={1.5} />
        </button>
        <h1 className="text-xl font-bold tracking-tight">Settings</h1>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-3">
          {settingsSections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id;
            return (
              <div key={section.id} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => {
                    if (section.isAction) {
                      window.dispatchEvent(new CustomEvent('sf-open-onboarding'));
                    } else if (section.isLink && onNavigate) {
                      onNavigate('sms-settings');
                    } else {
                      setExpandedSection(isExpanded ? null : section.id);
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
                  data-testid={`settings-section-${section.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-zinc-100 rounded-xl">
                      <Icon size={20} className="text-zinc-600" />
                    </div>
                    <div className="text-left">
                      <span className="text-zinc-900 font-semibold block">{section.name}</span>
                      <span className="text-xs text-zinc-500">{section.description}</span>
                    </div>
                  </div>
                  {(section.isLink || section.isAction) ? <ChevronRight size={20} className="text-zinc-400" /> : (isExpanded ? <ChevronDown size={20} className="text-zinc-400" /> : <ChevronRight size={20} className="text-zinc-400" />)}
                </button>
                
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-zinc-100">
                    <div className="pt-4">
                      {renderSectionContent(section.id)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 bg-white border-t border-zinc-200">
        <div className="text-center">
          <p className="text-sm font-medium text-zinc-600">StreakFree CRM</p>
          <p className="text-xs text-zinc-400">Version 1.53.5</p>
        </div>
      </div>

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <TemplateEditor onClose={() => setShowTemplateEditor(false)} />
      )}
      
      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
            <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-zinc-900">Add New User</h2>
              <button 
                onClick={() => setShowAddUserModal(false)}
                className="p-2 hover:bg-zinc-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {addUserError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  {addUserError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="john@company.com"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Password</label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  placeholder="Enter password for login"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-zinc-500 mt-1">User will use this password to log in</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Role</label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="Worker">Worker</option>
                  <option value="Planner">Planner</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-100 flex gap-3">
              <button
                onClick={() => setShowAddUserModal(false)}
                className="flex-1 py-2.5 text-zinc-600 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                disabled={addingUser}
                className="flex-1 py-2.5 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 disabled:opacity-50"
              >
                {addingUser ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* General Settings Edit Modal */}
      {showGeneralEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowGeneralEdit(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">
                {showGeneralEdit === 'currency' && 'Change Currency'}
                {showGeneralEdit === 'timezone' && 'Change Time Zone'}
                {showGeneralEdit === 'dateformat' && 'Change Date Format'}
                {showGeneralEdit === 'address' && 'Edit Business Address'}
              </h3>
              <button onClick={() => setShowGeneralEdit(null)} className="p-2 hover:bg-zinc-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              {showGeneralEdit === 'currency' && (
                <div className="space-y-2">
                  {['GBP', 'USD', 'EUR', 'AUD', 'CAD'].map(curr => (
                    <button
                      key={curr}
                      onClick={async () => {
                        setGeneralSettings(prev => ({ ...prev, currency: curr }));
                        // Save to backend
                        try {
                          const token = localStorage.getItem('streakfree_token');
                          await fetch(`${API_URL}/api/settings`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ currency: curr })
                          });
                        } catch (e) { console.error(e); }
                        setShowGeneralEdit(null);
                      }}
                      className={`w-full p-3 text-left rounded-lg transition-colors ${
                        generalSettings.currency === curr ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-50 hover:bg-zinc-100'
                      }`}
                    >
                      {curr} ({curr === 'GBP' ? '£' : curr === 'USD' ? '$' : curr === 'EUR' ? '€' : curr === 'AUD' ? 'A$' : 'C$'})
                    </button>
                  ))}
                </div>
              )}
              
              {showGeneralEdit === 'timezone' && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {['Europe/London', 'America/New_York', 'America/Los_Angeles', 'America/Chicago', 'Australia/Sydney', 'Asia/Tokyo', 'Europe/Paris'].map(tz => (
                    <button
                      key={tz}
                      onClick={async () => {
                        setGeneralSettings(prev => ({ ...prev, timezone: tz }));
                        try {
                          const token = localStorage.getItem('streakfree_token');
                          await fetch(`${API_URL}/api/settings`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ timezone: tz })
                          });
                        } catch (e) { console.error(e); }
                        setShowGeneralEdit(null);
                      }}
                      className={`w-full p-3 text-left rounded-lg transition-colors ${
                        generalSettings.timezone === tz ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-50 hover:bg-zinc-100'
                      }`}
                    >
                      {tz}
                    </button>
                  ))}
                </div>
              )}
              
              {showGeneralEdit === 'dateformat' && (
                <div className="space-y-2">
                  {['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].map(fmt => (
                    <button
                      key={fmt}
                      onClick={async () => {
                        setGeneralSettings(prev => ({ ...prev, dateFormat: fmt }));
                        try {
                          const token = localStorage.getItem('streakfree_token');
                          await fetch(`${API_URL}/api/settings`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ dateFormat: fmt })
                          });
                        } catch (e) { console.error(e); }
                        setShowGeneralEdit(null);
                      }}
                      className={`w-full p-3 text-left rounded-lg transition-colors ${
                        generalSettings.dateFormat === fmt ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-50 hover:bg-zinc-100'
                      }`}
                    >
                      {fmt} <span className="text-zinc-500 text-sm ml-2">(e.g., {fmt === 'DD/MM/YYYY' ? '11/03/2026' : fmt === 'MM/DD/YYYY' ? '03/11/2026' : '2026-03-11'})</span>
                    </button>
                  ))}
                </div>
              )}
              
              {showGeneralEdit === 'address' && (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={generalSettings.businessAddress}
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, businessAddress: e.target.value }))}
                    placeholder="Enter business address"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('streakfree_token');
                        await fetch(`${API_URL}/api/settings`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ businessAddress: generalSettings.businessAddress })
                        });
                      } catch (e) { console.error(e); }
                      setShowGeneralEdit(null);
                    }}
                    className="w-full py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600"
                  >
                    Save Address
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
